/**
 * Editor Visual de E-mail (estilo RD Station / BEE Editor)
 * Tela cheia com header, toolbar, painel esquerdo (3 abas), canvas central
 */

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import { useContextoDaTela } from "@/features/assistente/contexto-de-tela";
import { useLocation, useRoute } from "wouter";
import { MobileBlockerGate } from "@/components/MobileBlocker";
import { trpc } from "@/lib/trpc";
import { COMPANY } from "@shared/company-info";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail, Plus, Save, Eye,
  Type, Image as ImageIcon, Minus,
  ChevronUp, ChevronDown, Trash2, AlignLeft, AlignCenter,
  Users, X, GripVertical, Square, Palette,
  PlayCircle, List, Columns2,
  ArrowLeft, Monitor, Smartphone,
  LayoutTemplate, Rows3, Settings2,
  Copy, MoveUp, MoveDown,
  Upload, Link2, AlignRight,
  Hash, Sparkles, MoreVertical, Send,
  BookmarkPlus, Undo2, Redo2,
  Code2, GitBranch, Search, ChevronRight, ExternalLink,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { nanoid } from "nanoid";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";
import { Bold, Italic, Link as LinkIcon } from "lucide-react";
import { LeadCopyPanel, type ChatAction } from "@/components/leadcopy/LeadCopyPanel";
import { EmailDetailsBar } from "@/components/leadcopy/EmailDetailsBar";
import { ABTestPanel } from "@/components/leadcopy/ABTestPanel";
import AttachToFlowDialog from "@/components/email/AttachToFlowDialog";
import { VariablePicker, insertAtCursor } from "@/components/email/VariablePicker";
import { ImportHtmlDialog } from "@/components/email/ImportHtmlDialog";

// ─── Tipos de bloco ───────────────────────────────────────────────────────────

type BlockType = "header" | "text" | "image" | "button" | "divider" | "spacer" | "video" | "list" | "columns" | "social" | "customHtml";

interface Block {
  id: string;
  type: BlockType;
  // header
  title?: string;
  subtitle?: string;
  headerBg?: string;
  headerTextColor?: string;
  headerLogoUrl?: string;
  // text
  text?: string;
  align?: "left" | "center" | "right";
  fontSize?: number;
  // text — opcional "estilo de card" para preservar elementos decorativos
  // (cupom, código de rastreio, hero Black Friday). Quando qualquer um destes
  // está presente, o conteúdo é renderizado dentro de um wrapper estilizado.
  textColor?: string;
  bgColor?: string;
  borderColor?: string;
  borderStyle?: "solid" | "dashed" | "dotted";
  borderWidth?: number;
  borderRadius?: number;
  padding?: number;
  // image
  imageUrl?: string;
  imageAlt?: string;
  imageLink?: string;
  imageWidth?: number;
  // button
  btnText?: string;
  btnUrl?: string;
  btnBg?: string;
  btnTextColor?: string;
  btnAlign?: "left" | "center" | "right";
  btnBorderRadius?: number;
  // divider
  dividerColor?: string;
  dividerStyle?: "solid" | "dashed" | "dotted";
  dividerThickness?: number;
  dividerWidth?: number;
  // spacer
  spacerHeight?: number;
  // video
  videoUrl?: string;
  videoCaption?: string;
  // list
  listItems?: string;
  listStyle?: "bullet" | "numbered";
  // columns
  colLeftText?: string;
  colRightText?: string;
  colLeftImageUrl?: string;
  colRightImageUrl?: string;
  // social
  socialLinks?: { platform: string; url: string }[];
  socialStyle?: "colored" | "dark" | "light";
  // customHtml — bloco que renderiza um e-mail HTML completo (templates da galeria).
  // Usado pelos modelos prontos: o bloco contém o documento HTML inteiro e o editor
  // o renderiza/exporta sem o wrapper padrão (<!DOCTYPE><html><body><div.wrapper>).
  customHtml?: string;
}

const BLOCK_DEFAULTS: Record<BlockType, Partial<Block>> = {
  header: { title: "Olá, temos novidades!", subtitle: "", headerBg: "#2563eb", headerTextColor: "#ffffff" },
  text: { text: "Escreva o conteúdo do seu e-mail aqui. Use {{nome}} para personalizar com o nome do contato.", align: "left", fontSize: 15 },
  image: { imageUrl: "", imageAlt: "Imagem", imageLink: "", imageWidth: 100 },
  button: { btnText: "Saiba mais", btnUrl: "https://", btnBg: "#2563eb", btnTextColor: "#ffffff", btnAlign: "center", btnBorderRadius: 6 },
  divider: { dividerColor: "#e5e7eb", dividerStyle: "solid", dividerThickness: 1, dividerWidth: 100 },
  spacer: { spacerHeight: 24 },
  video: { videoUrl: "", videoCaption: "" },
  list: { listItems: "Primeiro item\nSegundo item\nTerceiro item", listStyle: "bullet", align: "left" },
  columns: { colLeftText: "Conteúdo da coluna esquerda", colRightText: "Conteúdo da coluna direita" },
  social: { socialLinks: [{ platform: "instagram", url: "" }, { platform: "facebook", url: "" }], socialStyle: "colored" },
  customHtml: { customHtml: "" },
};

const BLOCK_LABELS: Record<BlockType, string> = {
  header: "Título",
  text: "Parágrafo",
  image: "Imagem",
  button: "Botão",
  divider: "Divisor",
  spacer: "Distanciador",
  video: "Vídeo",
  list: "Lista",
  columns: "Colunas",
  social: "Redes Sociais",
  customHtml: "HTML personalizado",
};

const BLOCK_ICONS: Record<BlockType, any> = {
  header: Type,
  text: AlignLeft,
  image: ImageIcon,
  button: Square,
  divider: Minus,
  spacer: MoveDown,
  video: PlayCircle,
  list: List,
  columns: Columns2,
  social: Hash,
  customHtml: Code2,
};

// ─── Layouts de linha predefinidos ────────────────────────────────────────────

interface RowLayout {
  id: string;
  label: string;
  columns: number[];
  preview: string;
}

const ROW_LAYOUTS: RowLayout[] = [
  { id: "1col", label: "1 coluna", columns: [100], preview: "████████████" },
  { id: "2col-equal", label: "2 colunas iguais", columns: [50, 50], preview: "██████ ██████" },
  { id: "2col-left", label: "1/3 + 2/3", columns: [33, 67], preview: "████ ████████" },
  { id: "2col-right", label: "2/3 + 1/3", columns: [67, 33], preview: "████████ ████" },
  { id: "3col", label: "3 colunas", columns: [33, 34, 33], preview: "████ ████ ████" },
  { id: "4col", label: "4 colunas", columns: [25, 25, 25, 25], preview: "███ ███ ███ ███" },
];

// ─── Design Settings ──────────────────────────────────────────────────────────

interface DesignSettings {
  contentWidth: number;
  contentAlign: "left" | "center";
  bgColor: string;
  contentBgColor: string;
  fontFamily: string;
  linkColor: string;
}

const DEFAULT_DESIGN: DesignSettings = {
  contentWidth: 600,
  contentAlign: "center",
  bgColor: "#f3f4f6",
  contentBgColor: "#ffffff",
  fontFamily: "Arial, Helvetica, sans-serif",
  linkColor: "#2563eb",
};

const FONT_OPTIONS = [
  "Arial, Helvetica, sans-serif",
  "Georgia, serif",
  "Times New Roman, Times, serif",
  "Verdana, Geneva, sans-serif",
  "Trebuchet MS, Helvetica, sans-serif",
  "Courier New, Courier, monospace",
  "Tahoma, Geneva, sans-serif",
];

// ─── HTML → markdown (limpeza dos templates legados) ────────────────────────
//
// Inverso do `mdToHtml`. Templates antigos têm `<strong>`, `<em>`, `<a>`,
// `<br>` e até `<div>`/`<p>` aninhados no campo `text`. Pra que o textarea
// mostre conteúdo legível (sem HTML cru) e a edição inline funcione com o
// mesmo formato dos templates novos, normalizamos pra markdown leve no load.
//
// É idempotente: texto que já está em markdown passa direto.

const ENTITY_MAP: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
};

function decodeHtmlEntities(s: string): string {
  return s.replace(/&(?:nbsp|amp|lt|gt|quot|#39|apos);/g, (m) => ENTITY_MAP[m] ?? m);
}

function htmlToMd(html: string): string {
  if (!html) return "";
  let s = html;
  s = decodeHtmlEntities(s);
  // Strip wrapping spans (style/class on inline tags is dropped along the way).
  s = s.replace(/<\/?span\b[^>]*>/gi, "");
  // <strong>/<b> → **markdown**
  s = s.replace(/<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi, (_m, _t, body) => {
    const t = String(body).replace(/<[^>]+>/g, "").trim();
    return t ? `**${t}**` : "";
  });
  // <em>/<i> → *markdown*
  s = s.replace(/<(em|i)\b[^>]*>([\s\S]*?)<\/\1>/gi, (_m, _t, body) => {
    const t = String(body).replace(/<[^>]+>/g, "").trim();
    return t ? `*${t}*` : "";
  });
  // <a href="U">L</a> → [L](U)
  s = s.replace(/<a\b[^>]*\bhref="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, url, label) => {
    const t = String(label).replace(/<[^>]+>/g, "").trim();
    return t ? `[${t}](${url})` : "";
  });
  // <br> / <br/> → \n
  s = s.replace(/<br\s*\/?>/gi, "\n");
  // Headings → bold paragraph.
  s = s.replace(/<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>/gi, (_m, body) => {
    const t = String(body).replace(/<[^>]+>/g, "").trim();
    return t ? `**${t}**\n\n` : "";
  });
  // <p>/<div> → conteúdo + \n\n. Trailing newline garante separação.
  s = s.replace(/<\/(?:p|div)>/gi, "\n\n");
  // Open tags <p ...> / <div ...> são removidas (deixam o conteúdo).
  s = s.replace(/<(?:p|div)\b[^>]*>/gi, "");
  // Strip qualquer outra tag remanescente (table interno, etc.)
  s = s.replace(/<[^>]+>/g, "");
  // Limpa espaços / quebras excessivas.
  s = s.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return s;
}

// Detecta um wrapper `<div style="background:...; padding:...;">` no início
// do HTML legado e extrai pra props de "card" do bloco. Devolve `{ inner, card }`
// — `inner` é o HTML sem o wrapper, `card` são os campos a colocar no bloco.
function extractOuterCardWrapper(html: string): {
  inner: string;
  card: Partial<Pick<Block, "bgColor" | "borderColor" | "borderStyle" | "borderWidth" | "borderRadius" | "padding">>;
} {
  const m = /^\s*<div\b[^>]*\bstyle=("|')([^"']+)\1[^>]*>([\s\S]*)<\/div>\s*$/i.exec(html);
  if (!m) return { inner: html, card: {} };
  const style = m[2];
  const inner = m[3];
  const get = (prop: string) => {
    const r = new RegExp("(?:^|;)\\s*" + prop + "\\s*:\\s*([^;]+)", "i").exec(style);
    return r ? r[1].trim() : "";
  };
  const bg = get("background-color") || get("background");
  const radius = get("border-radius");
  const padding = get("padding");
  const card: Record<string, unknown> = {};
  // Só extrai se o div parecer um "card" mesmo (tem bg ou border).
  const looksLikeCard = !!bg || /border/i.test(style);
  if (!looksLikeCard) return { inner: html, card: {} };
  if (bg && bg !== "transparent") card.bgColor = bg;
  if (radius) {
    const n = parseInt(radius, 10);
    if (!Number.isNaN(n)) card.borderRadius = n;
  }
  if (padding) {
    const n = parseInt(padding, 10);
    if (!Number.isNaN(n)) card.padding = n;
  }
  return { inner, card };
}

// ─── Normalização de bloco (compat. legacy `props.X`) ───────────────────────
//
// Templates antigos guardavam os atributos do bloco aninhados em `props`
// (`{ type: "text", props: { content, fontSize, alignment } }`). O editor
// atual usa schema "flat" (`{ type: "text", text, fontSize, align }`). Esta
// função migra qualquer bloco no formato antigo para o atual no momento em
// que a campanha é carregada — assim os modelos prontos legados continuam
// editáveis sem precisar reescrever os dados na origem.
//
// Também converte HTML embutido em campos de texto pra markdown leve, pro
// textarea mostrar conteúdo legível e a edição inline funcionar uniformemente.

function normalizeBlock(raw: Record<string, any>): Block {
  if (!raw || typeof raw !== "object") return raw as Block;
  // Já está no schema flat — só roda a higienização final (conversão HTML→md
  // do campo text se ainda houver HTML cru).
  if (!raw.props || typeof raw.props !== "object") {
    return finalizeNormalized(raw as Block);
  }
  const p = raw.props;
  const out: Record<string, any> = { id: raw.id, type: raw.type };
  switch (raw.type) {
    case "image":
      if (p.imageUrl !== undefined) out.imageUrl = p.imageUrl;
      if (p.alt !== undefined) out.imageAlt = p.alt;
      if (p.width !== undefined) out.imageWidth = p.width;
      if (p.link !== undefined) out.imageLink = p.link;
      break;
    case "text":
      if (p.content !== undefined) out.text = p.content;
      if (p.text !== undefined) out.text = p.text;
      if (p.fontSize !== undefined) out.fontSize = p.fontSize;
      if (p.alignment !== undefined) out.align = p.alignment;
      if (p.align !== undefined) out.align = p.align;
      break;
    case "header":
      if (p.title !== undefined) out.title = p.title;
      if (p.subtitle !== undefined) out.subtitle = p.subtitle;
      if (p.bg !== undefined) out.headerBg = p.bg;
      if (p.backgroundColor !== undefined) out.headerBg = p.backgroundColor;
      if (p.textColor !== undefined) out.headerTextColor = p.textColor;
      if (p.logoUrl !== undefined) out.headerLogoUrl = p.logoUrl;
      break;
    case "button":
      if (p.text !== undefined) out.btnText = p.text;
      if (p.url !== undefined) out.btnUrl = p.url;
      if (p.backgroundColor !== undefined) out.btnBg = p.backgroundColor;
      if (p.textColor !== undefined) out.btnTextColor = p.textColor;
      if (p.borderRadius !== undefined) out.btnBorderRadius = p.borderRadius;
      if (p.alignment !== undefined) out.btnAlign = p.alignment;
      break;
    case "divider":
      if (p.color !== undefined) out.dividerColor = p.color;
      if (p.thickness !== undefined) out.dividerThickness = p.thickness;
      if (p.style !== undefined) out.dividerStyle = p.style;
      if (p.width !== undefined) out.dividerWidth = p.width;
      break;
    case "spacer":
      if (p.height !== undefined) out.spacerHeight = p.height;
      break;
    case "video":
      if (p.url !== undefined) out.videoUrl = p.url;
      if (p.caption !== undefined) out.videoCaption = p.caption;
      break;
    case "list":
      if (p.items !== undefined) out.listItems = Array.isArray(p.items) ? p.items.join("\n") : p.items;
      if (p.style !== undefined) out.listStyle = p.style;
      if (p.alignment !== undefined) out.align = p.alignment;
      break;
    case "social": {
      // Old shape: networks: [{ name: "Instagram", url: "..." }]
      // New shape: socialLinks: [{ platform: "instagram", url: "..." }]
      const nets = p.networks || p.socialLinks;
      if (Array.isArray(nets)) {
        out.socialLinks = nets.map((n: any) => ({
          platform: String(n.platform || n.name || "").toLowerCase(),
          url: n.url || "",
        }));
      }
      if (p.style !== undefined) out.socialStyle = p.style;
      break;
    }
    case "customHtml":
      if (p.html !== undefined) out.customHtml = p.html;
      if (p.customHtml !== undefined) out.customHtml = p.customHtml;
      break;
  }
  return finalizeNormalized(out as Block);
}

// Higienização final aplicada em todo bloco que passa por `normalizeBlock`,
// independente de já estar no schema flat ou ter vindo de `props.X`. Converte
// HTML cru em markdown e detecta wrapper de "card" pra extrair pra props.
function finalizeNormalized(b: Block): Block {
  if (b.type !== "text") return b;
  const text = b.text;
  if (typeof text !== "string" || text.length === 0) return b;
  // Sem tags HTML → já está em markdown puro, não toca.
  if (!/<[a-z][a-z0-9]*\b/i.test(text)) return b;
  const out: Record<string, unknown> = { ...b };
  // Detecta wrapper externo de card (ex.: "Boas-vindas" legado tem
  // <div style="background:#f8fafc;border-radius:8px;padding:20px;">...</div>).
  // Extrai pros campos de estilo do bloco se ainda não estiverem definidos.
  const { inner, card } = extractOuterCardWrapper(text);
  for (const [k, v] of Object.entries(card)) {
    if ((out as Record<string, unknown>)[k] === undefined) out[k] = v;
  }
  out.text = htmlToMd(inner);
  return out as unknown as Block;
}

// ─── Markdown leve → HTML para o conteúdo dos blocos de texto ───────────────
//
// O conteúdo de um bloco `text` é editado num textarea simples. Para que o
// usuário não precise digitar HTML, suportamos um subset de markdown que cobre
// 99% dos casos dos templates: negrito (`**texto**`), itálico (`*texto*`),
// links `[texto](url)` e quebras de linha (uma quebra "real" no textarea vira
// `<br>` no e-mail). Tudo é idempotente: HTML já presente passa direto.

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function mdToHtml(text: string, linkColor: string): string {
  if (!text) return "";
  let s = text;
  // Bold: **texto** → <strong>texto</strong>
  s = s.replace(/\*\*([^*\n]+?)\*\*/g, "<strong>$1</strong>");
  // Italic: *texto* (single asterisks, not adjacent to **)
  s = s.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");
  // Links: [texto](url) → <a href="url">texto</a>
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) =>
    `<a href="${url}" style="color:${linkColor};text-decoration:underline">${label}</a>`
  );
  // Newlines → <br> (preserve double-newline as double <br>)
  s = s.replace(/\r\n/g, "\n").replace(/\n/g, "<br>");
  return s;
}
void escapeRegex; // reserved for future use

// ─── Render de bloco no preview ──────────────────────────────────────────────

function renderBlockHtml(block: Block, design: DesignSettings): string {
  const fontFamily = design.fontFamily;
  const linkColor = design.linkColor;

  switch (block.type) {
    case "header":
      return `<div style="background:${block.headerBg ?? "#2563eb"};padding:32px 24px;text-align:center;font-family:${fontFamily}">
        ${block.headerLogoUrl ? `<img src="${block.headerLogoUrl}" style="max-height:50px;margin-bottom:12px;display:block;margin-left:auto;margin-right:auto" alt="Logo">` : ""}
        <h1 style="margin:0;font-size:24px;font-weight:700;color:${block.headerTextColor ?? "#ffffff"};line-height:1.3">${block.title ?? ""}</h1>
        ${block.subtitle ? `<p style="margin:8px 0 0;font-size:15px;color:${block.headerTextColor ?? "#ffffff"};opacity:0.85">${block.subtitle}</p>` : ""}
      </div>`;
    case "text": {
      const content = block.text ?? "";
      const hasBlockEl = /<(table|div|ul|ol|h[1-6]|blockquote|hr|pre)\b/i.test(content);
      const align = block.align ?? "left";
      const size = block.fontSize ?? 15;
      const tcolor = block.textColor ?? "#374151";
      // Plain content goes through markdown→HTML so the textarea can stay
      // markdown-only (`**bold**`, `[link](url)`, real newlines). Block-level
      // HTML (table/div/list) is rendered as-is.
      const renderedContent = hasBlockEl ? content : mdToHtml(content, design.linkColor);
      // Optional card styling — used by decorative templates (cupom, tracking
      // code, hero Black Friday). When any of these is set, wrap content in a
      // styled inner card so the visual identity is preserved.
      const hasCard = !!(block.bgColor || block.borderColor);
      if (hasCard) {
        const bw = block.borderWidth ?? (block.borderColor ? 1 : 0);
        const bs = block.borderStyle ?? "solid";
        const bg = block.bgColor ?? "transparent";
        const bc = block.borderColor;
        const br = block.borderRadius ?? 8;
        const pad = block.padding ?? 24;
        const cardStyle = `background:${bg};${bc ? `border:${bw}px ${bs} ${bc};` : ""}border-radius:${br}px;padding:${pad}px;text-align:${align};color:${tcolor};font-family:${fontFamily};font-size:${size}px;line-height:1.5;`;
        const inner = hasBlockEl
          ? content
          : `<div style="margin:0;color:${tcolor}">${renderedContent}</div>`;
        return `<div style="padding:12px 24px;font-family:${fontFamily}"><div style="${cardStyle}">${inner}</div></div>`;
      }
      if (hasBlockEl) {
        return `<div style="padding:20px 24px;text-align:${align};font-family:${fontFamily};font-size:${size}px;line-height:1.7;color:${tcolor}">${content}</div>`;
      }
      return `<div style="padding:20px 24px;text-align:${align};font-family:${fontFamily}">
        <p style="margin:0;font-size:${size}px;line-height:1.7;color:${tcolor}">${renderedContent}</p>
      </div>`;
    }
    case "image":
      if (!block.imageUrl) return `<div style="padding:16px 24px;text-align:center;background:#f9fafb;color:#9ca3af;font-size:13px;font-family:${fontFamily}">Imagem não configurada</div>`;
      const imgWidth = block.imageWidth ?? 100;
      const imgTag = `<img src="${block.imageUrl}" alt="${block.imageAlt ?? ""}" style="max-width:${imgWidth}%;height:auto;border-radius:4px;display:block;margin:0 auto">`;
      return `<div style="padding:16px 24px;text-align:center">${block.imageLink ? `<a href="${block.imageLink}">${imgTag}</a>` : imgTag}</div>`;
    case "button":
      return `<div style="padding:16px 24px;text-align:${block.btnAlign ?? "center"};font-family:${fontFamily}">
        <a href="${block.btnUrl ?? "#"}" style="display:inline-block;background:${block.btnBg ?? "#2563eb"};color:${block.btnTextColor ?? "#ffffff"};padding:12px 28px;border-radius:${block.btnBorderRadius ?? 6}px;text-decoration:none;font-size:15px;font-weight:600">${block.btnText ?? "Clique aqui"}</a>
      </div>`;
    case "divider": {
      const w = block.dividerWidth ?? 100;
      return `<div style="padding:8px 24px"><hr style="border:none;border-top:${block.dividerThickness ?? 1}px ${block.dividerStyle ?? "solid"} ${block.dividerColor ?? "#e5e7eb"};margin:0;width:${w}%;${w < 100 ? "margin-left:auto;margin-right:auto" : ""}"></div>`;
    }
    case "spacer":
      return `<div style="height:${block.spacerHeight ?? 24}px"></div>`;
    case "video": {
      const url = block.videoUrl ?? "";
      if (!url) return `<div style="padding:16px 24px;text-align:center;background:#f9fafb;color:#9ca3af;font-size:13px;font-family:${fontFamily}">Vídeo não configurado</div>`;
      let thumbUrl = "https://via.placeholder.com/600x338?text=Video";
      const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
      if (ytMatch) thumbUrl = `https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`;
      return `<div style="padding:16px 24px;text-align:center">
        <a href="${url}" style="display:block;position:relative;text-decoration:none">
          <img src="${thumbUrl}" alt="Video thumbnail" style="max-width:100%;height:auto;border-radius:8px;display:block;margin:0 auto">
          <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:60px;height:60px;background:rgba(0,0,0,0.65);border-radius:50%;display:flex;align-items:center;justify-content:center">
            <div style="width:0;height:0;border-style:solid;border-width:10px 0 10px 20px;border-color:transparent transparent transparent #ffffff;margin-left:4px"></div>
          </div>
        </a>
        ${block.videoCaption ? `<p style="margin:8px 0 0;font-size:13px;color:#6b7280;font-family:${fontFamily}">${block.videoCaption}</p>` : ""}
      </div>`;
    }
    case "list": {
      const items = (block.listItems ?? "").split("\n").filter(l => l.trim());
      const tag = block.listStyle === "numbered" ? "ol" : "ul";
      const listStyleType = block.listStyle === "numbered" ? "decimal" : "disc";
      const liHtml = items.map(item => `<li style="margin-bottom:6px;font-size:15px;line-height:1.6;color:#374151">${item}</li>`).join("");
      return `<div style="padding:16px 24px;text-align:${block.align ?? "left"};font-family:${fontFamily}">
        <${tag} style="margin:0;padding-left:24px;list-style-type:${listStyleType}">${liHtml}</${tag}>
      </div>`;
    }
    case "columns":
      return `<div style="padding:16px 24px;font-family:${fontFamily}">
        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse">
          <tr>
            <td width="50%" style="padding-right:12px;vertical-align:top">
              ${block.colLeftImageUrl ? `<img src="${block.colLeftImageUrl}" alt="" style="max-width:100%;height:auto;border-radius:4px;display:block;margin-bottom:8px">` : ""}
              <p style="margin:0;font-size:14px;line-height:1.6;color:#374151">${block.colLeftText ?? ""}</p>
            </td>
            <td width="50%" style="padding-left:12px;vertical-align:top">
              ${block.colRightImageUrl ? `<img src="${block.colRightImageUrl}" alt="" style="max-width:100%;height:auto;border-radius:4px;display:block;margin-bottom:8px">` : ""}
              <p style="margin:0;font-size:14px;line-height:1.6;color:#374151">${block.colRightText ?? ""}</p>
            </td>
          </tr>
        </table>
      </div>`;
    case "social": {
      const links = block.socialLinks ?? [];
      const style = block.socialStyle ?? "colored";
      const colors: Record<string, string> = {
        instagram: "#E4405F", facebook: "#1877F2", twitter: "#1DA1F2",
        linkedin: "#0A66C2", youtube: "#FF0000", tiktok: "#000000", whatsapp: "#25D366",
      };
      const iconsHtml = links.filter(l => l.url).map(l => {
        const bg = style === "colored" ? (colors[l.platform] ?? "#333") : style === "dark" ? "#333" : "#e5e7eb";
        const textColor = style === "light" ? "#333" : "#fff";
        return `<a href="${l.url}" style="display:inline-block;width:36px;height:36px;border-radius:50%;background:${bg};color:${textColor};text-align:center;line-height:36px;margin:0 4px;text-decoration:none;font-size:14px;font-weight:bold">${l.platform.charAt(0).toUpperCase()}</a>`;
      }).join("");
      return `<div style="padding:16px 24px;text-align:center">${iconsHtml || '<span style="color:#9ca3af;font-size:13px">Configure suas redes sociais</span>'}</div>`;
    }
    case "customHtml":
      return block.customHtml ?? "";
    default:
      return "";
  }
}

function buildEmailHtml(blocks: Block[], footer: string, design: DesignSettings): string {
  // Templates da galeria são entregues como um único bloco `customHtml` contendo o
  // documento HTML inteiro. Nesse caso, devolvemos o HTML cru — sem wrapper, sem
  // header/footer de compliance — porque o template já tem seu próprio doctype,
  // estrutura responsiva e rodapé com link de descadastro.
  if (blocks.length === 1 && blocks[0].type === "customHtml" && blocks[0].customHtml) {
    return blocks[0].customHtml;
  }

  const contentWidth = design.contentWidth;
  const bgColor = design.bgColor;
  const contentBgColor = design.contentBgColor;
  const fontFamily = design.fontFamily;
  const align = design.contentAlign === "center" ? "margin:0 auto" : "margin:0";

  const footerText = footer || `© ${new Date().getFullYear()} ${COMPANY.legalEntity}. Todos os direitos reservados.`;
  // Bloco de compliance: identificação + endereço físico + link de privacidade.
  // O link de descadastro é injetado automaticamente pelo SendGrid via subscription_tracking
  // (ver server/sendgrid.ts), o que também popula o header List-Unsubscribe.
  const complianceBlock = `
    <div style="margin-top:8px;font-size:11px;color:#9ca3af;line-height:1.55">
      ${COMPANY.legalEntity} — CNPJ ${COMPANY.cnpj}<br>
      ${COMPANY.addressFormatted}
    </div>
    <div style="margin-top:10px;font-size:12px">
      <a href="${COMPANY.privacyUrl}" style="color:#9ca3af;text-decoration:underline">Política de Privacidade</a>
    </div>`;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
  <style>body{margin:0;padding:0;background:${bgColor};font-family:${fontFamily}}.wrapper{max-width:${contentWidth}px;${align};background:${contentBgColor};border-radius:0;overflow:hidden}</style>
  </head><body><div class="wrapper">
  ${blocks.map(b => renderBlockHtml(b, design)).join("")}
  <div style="background:#f9fafb;padding:16px 24px;text-align:center;font-size:12px;color:#9ca3af;border-top:1px solid #e5e7eb;font-family:${fontFamily}">${footerText}${complianceBlock}</div>
  </div></body></html>`;
}

// ─── Edição inline no canvas ────────────────────────────────────────────────
//
// `EditableBlockCanvas` renderiza o bloco direto no canvas, com `contentEditable`
// nos elementos textuais (`text` → corpo do parágrafo, `header` → título e
// subtítulo, `button` → label do botão). Demais tipos caem no render estático
// (HTML sanitizado) — não há texto a editar inline neles. O salvamento usa
// `onBlur`: lê o `innerHTML`, converte HTML → markdown e propaga via `onUpdate`.
//
// Padrão "uncontrolled": o conteúdo é injetado via `dangerouslySetInnerHTML`
// uma única vez por `block.id`. Mudanças vindas de fora (painel direito) são
// reaplicadas em DOM por `useEffect`, mas só quando o texto externo difere do
// último valor capturado localmente — assim o cursor não pula durante a
// digitação.

const STOP = (e: React.SyntheticEvent) => e.stopPropagation();

function CanvasInlineEditableHtml({
  blockId,
  initialText,
  linkColor,
  onCommit,
  className,
  style,
  marker,
}: {
  blockId: string;
  initialText: string;
  linkColor: string;
  onCommit: (newText: string) => void;
  className?: string;
  style?: React.CSSProperties;
  marker: "text" | "card";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef(initialText);
  // Re-syncroniza o DOM quando o texto vem de fora (BlockEditor do painel) e é
  // diferente do que escrevemos no último blur. Não dispara durante a digitação.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (initialText !== lastValueRef.current) {
      el.innerHTML = DOMPurify.sanitize(mdToHtml(initialText, linkColor));
      lastValueRef.current = initialText;
    }
  }, [initialText, linkColor]);
  return (
    <div
      ref={ref}
      key={blockId}
      contentEditable
      suppressContentEditableWarning
      data-editable-text={marker}
      data-block-id={blockId}
      onMouseDown={STOP}
      onClick={STOP}
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mdToHtml(initialText, linkColor)) }}
      onBlur={(e) => {
        const newMd = htmlToMd(e.currentTarget.innerHTML);
        if (newMd !== lastValueRef.current) {
          lastValueRef.current = newMd;
          onCommit(newMd);
        }
      }}
    />
  );
}

function CanvasInlineEditablePlain({
  blockId,
  initialText,
  onCommit,
  className,
  style,
  placeholder,
  marker,
}: {
  blockId: string;
  initialText: string;
  onCommit: (newText: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  marker: "title" | "subtitle" | "button";
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const lastValueRef = useRef(initialText);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (initialText !== lastValueRef.current) {
      el.textContent = initialText;
      lastValueRef.current = initialText;
    }
  }, [initialText]);
  return (
    <span
      ref={ref}
      key={blockId}
      contentEditable
      suppressContentEditableWarning
      data-editable-text={marker}
      data-block-id={blockId}
      onMouseDown={STOP}
      onClick={STOP}
      data-placeholder={placeholder}
      className={className}
      style={style}
      // textContent — single-line plain text fields (title, subtitle, button label)
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(initialText) }}
      onBlur={(e) => {
        const newText = (e.currentTarget.textContent ?? "").trim();
        if (newText !== lastValueRef.current) {
          lastValueRef.current = newText;
          onCommit(newText);
        }
      }}
      onKeyDown={(e) => {
        // Single-line: Enter dispara blur (sem inserir <br>).
        if (e.key === "Enter") {
          e.preventDefault();
          (e.currentTarget as HTMLElement).blur();
        }
      }}
    />
  );
}

function CanvasTextBlock({
  block,
  design,
  onUpdate,
}: {
  block: Block;
  design: DesignSettings;
  onUpdate: (updates: Partial<Block>) => void;
}) {
  const align = block.align ?? "left";
  const size = block.fontSize ?? 15;
  const tcolor = block.textColor ?? "#374151";
  const fontFamily = design.fontFamily;
  const hasCard = !!(block.bgColor || block.borderColor);

  if (hasCard) {
    const bw = block.borderWidth ?? (block.borderColor ? 1 : 0);
    const bs = block.borderStyle ?? "solid";
    const bg = block.bgColor ?? "transparent";
    const bc = block.borderColor;
    const br = block.borderRadius ?? 8;
    const pad = block.padding ?? 24;
    const cardStyle: React.CSSProperties = {
      background: bg,
      border: bc ? `${bw}px ${bs} ${bc}` : undefined,
      borderRadius: br,
      padding: pad,
      textAlign: align,
      color: tcolor,
      fontFamily,
      fontSize: size,
      lineHeight: 1.5,
    };
    return (
      <div style={{ padding: "12px 24px", fontFamily }}>
        <div style={cardStyle}>
          <CanvasInlineEditableHtml
            blockId={block.id}
            initialText={block.text ?? ""}
            linkColor={design.linkColor}
            onCommit={(t) => onUpdate({ text: t })}
            className="outline-none cursor-text"
            style={{ minHeight: "1em" }}
            marker="card"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px", textAlign: align, fontFamily }}>
      <CanvasInlineEditableHtml
        blockId={block.id}
        initialText={block.text ?? ""}
        linkColor={design.linkColor}
        onCommit={(t) => onUpdate({ text: t })}
        className="outline-none cursor-text"
        style={{ margin: 0, fontSize: size, lineHeight: 1.7, color: tcolor, minHeight: "1em" }}
        marker="text"
      />
    </div>
  );
}

function CanvasHeaderBlock({
  block,
  design,
  onUpdate,
}: {
  block: Block;
  design: DesignSettings;
  onUpdate: (updates: Partial<Block>) => void;
}) {
  const fontFamily = design.fontFamily;
  return (
    <div
      style={{
        background: block.headerBg ?? "#2563eb",
        padding: "32px 24px",
        textAlign: "center",
        fontFamily,
      }}
    >
      {block.headerLogoUrl && (
        <img
          src={block.headerLogoUrl}
          alt="Logo"
          style={{ maxHeight: 50, marginBottom: 12, display: "block", marginLeft: "auto", marginRight: "auto" }}
        />
      )}
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: block.headerTextColor ?? "#ffffff", lineHeight: 1.3 }}>
        <CanvasInlineEditablePlain
          blockId={block.id}
          initialText={block.title ?? ""}
          onCommit={(t) => onUpdate({ title: t })}
          className="outline-none cursor-text inline-block min-w-[2em]"
          placeholder="Título"
          marker="title"
        />
      </h1>
      {(block.subtitle !== undefined && block.subtitle !== null) && (
        <p style={{ margin: "8px 0 0", fontSize: 15, color: block.headerTextColor ?? "#ffffff", opacity: 0.85 }}>
          <CanvasInlineEditablePlain
            blockId={block.id + "-sub"}
            initialText={block.subtitle ?? ""}
            onCommit={(t) => onUpdate({ subtitle: t })}
            className="outline-none cursor-text inline-block min-w-[2em]"
            placeholder="Subtítulo (opcional)"
            marker="subtitle"
          />
        </p>
      )}
    </div>
  );
}

function CanvasButtonBlock({
  block,
  design,
  onUpdate,
}: {
  block: Block;
  design: DesignSettings;
  onUpdate: (updates: Partial<Block>) => void;
}) {
  const fontFamily = design.fontFamily;
  const align = block.btnAlign ?? "center";
  return (
    <div style={{ padding: "16px 24px", textAlign: align, fontFamily }}>
      <a
        href={block.btnUrl ?? "#"}
        onClick={(e) => e.preventDefault()}
        style={{
          display: "inline-block",
          background: block.btnBg ?? "#2563eb",
          color: block.btnTextColor ?? "#ffffff",
          padding: "12px 28px",
          borderRadius: block.btnBorderRadius ?? 6,
          textDecoration: "none",
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        <CanvasInlineEditablePlain
          blockId={block.id}
          initialText={block.btnText ?? ""}
          onCommit={(t) => onUpdate({ btnText: t })}
          className="outline-none cursor-text"
          placeholder="Texto do botão"
          marker="button"
        />
      </a>
    </div>
  );
}

function EditableBlockCanvas({
  block,
  design,
  onUpdate,
  isSelected,
  onActivate,
  onRegisterCustomHtmlFlush,
}: {
  block: Block;
  design: DesignSettings;
  onUpdate: (updates: Partial<Block>) => void;
  isSelected?: boolean;
  onActivate?: () => void;
  /** Propagado só pra blocos customHtml — registra o flush imperativo no parent. */
  onRegisterCustomHtmlFlush?: (flushFn: (() => string | null) | null) => void;
}) {
  if (block.type === "text") return <CanvasTextBlock block={block} design={design} onUpdate={onUpdate} />;
  if (block.type === "header") return <CanvasHeaderBlock block={block} design={design} onUpdate={onUpdate} />;
  if (block.type === "button") return <CanvasButtonBlock block={block} design={design} onUpdate={onUpdate} />;
  // customHtml contém um documento HTML completo (templates importados): renderizar
  // inline via dangerouslySetInnerHTML faz o `<style>` global vazar para o app e
  // bagunçar a página inteira. Isolamos em iframe (srcDoc) — sandbox sem scripts.
  if (block.type === "customHtml") return (
    <CustomHtmlIframe
      html={block.customHtml ?? ""}
      onHtmlChange={(next) => onUpdate({ customHtml: next })}
      isSelected={!!isSelected}
      onActivate={onActivate}
      onRegisterFlush={onRegisterCustomHtmlFlush}
    />
  );
  // Outros tipos ficam estáticos no canvas; edição via painel direito.
  return (
    <div
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderBlockHtml(block, design)) }}
      className="pointer-events-none"
    />
  );
}

// Iframe sandboxado para renderizar HTML importado COM EDIÇÃO INLINE.
//
// Sandbox: `allow-same-origin` permite que o parent acesse o contentDocument
// (necessário para ResizeObserver e designMode). Não passamos `allow-scripts`
// — defesa em profundidade contra `<script>` que tenha escapado da sanitização.
//
// Edição: ativamos `designMode = "on"` no documento → todo o conteúdo vira
// editável (texto, links, células de tabela). É um modo nativo do browser,
// não precisa de JS dentro do iframe. Mudanças voltam pro parent state via
// listener de `input` (debounce de 400ms).
//
// Altura: ResizeObserver sobre `<html>` interno; cresce conforme imagens
// carregam, fontes trocam, ou usuário digita texto novo.
//
// Render: a primeira renderização usa `srcDoc`. Como o parent envia o html
// de volta pra cá em cada keystroke (após debounce), evitamos o re-render
// do srcDoc enquanto o usuário edita — checamos se a mudança veio "de fora"
// (programática) ou do próprio iframe.
function CustomHtmlIframe({
  html,
  onHtmlChange,
  isSelected,
  onActivate,
  onRegisterFlush,
}: {
  html: string;
  onHtmlChange?: (next: string) => void;
  isSelected?: boolean;
  onActivate?: () => void;
  /**
   * Registra uma função síncrona que lê o `outerHTML` atual do iframe
   * (cancelando qualquer debounce pendente). O parent guarda essa função
   * num ref e a chama antes de salvar — assim o save NUNCA pega HTML stale
   * por causa do debounce de 150ms entre tecla e setState. Retorna o HTML
   * serializado para uso imediato (bypassa React batching).
   */
  onRegisterFlush?: (flushFn: (() => string | null) | null) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(400);
  // Guarda o último valor que NÓS emitimos pra fora via onHtmlChange.
  // Quando a prop `html` chega de volta igual a esse ref, sabemos que veio
  // do nosso próprio input handler (echo) — não devemos re-renderizar o
  // srcDoc, senão o cursor pula. Quando `html` diverge, é mudança externa
  // (find&replace, import novo) e precisamos remontar o iframe.
  const lastEmittedRef = useRef<string>(html);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // onHtmlChange via ref: parents que passam função inline (não memoizada)
  // mudariam a referência a cada render, invalidando o effect e remontando
  // ResizeObserver/designMode/listeners a cada keystroke debouncado. Lendo
  // via ref mantém o callback fresh sem precisar dele na dep list.
  const onHtmlChangeRef = useRef(onHtmlChange);
  useEffect(() => { onHtmlChangeRef.current = onHtmlChange; }, [onHtmlChange]);
  // Mesmo padrão para onRegisterFlush — evita re-rodar o effect só porque
  // o parent não memoizou a callback.
  const onRegisterFlushRef = useRef(onRegisterFlush);
  useEffect(() => { onRegisterFlushRef.current = onRegisterFlush; }, [onRegisterFlush]);
  const editable = !!onHtmlChange;

  // srcDoc é gerenciado por estado — só atualiza em mudança externa, nunca
  // em response ao próprio input do usuário (preserva cursor).
  const [srcDoc, setSrcDoc] = useState(() =>
    DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true }),
  );

  useEffect(() => {
    if (html !== lastEmittedRef.current) {
      lastEmittedRef.current = html;
      setSrcDoc(DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true }));
    }
  }, [html]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let ro: ResizeObserver | null = null;
    let imageListeners: { img: HTMLImageElement; fn: () => void }[] = [];
    let pollTimeouts: ReturnType<typeof setTimeout>[] = [];
    let inputHandler: (() => void) | null = null;
    let linkNavBlocker: ((e: Event) => void) | null = null;
    let focusoutHandler: (() => void) | null = null;
    let registeredFlushFn: (() => string | null) | null = null;

    const measure = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const candidates = [
        doc.documentElement.scrollHeight,
        doc.body?.scrollHeight ?? 0,
        doc.documentElement.offsetHeight,
        doc.body?.offsetHeight ?? 0,
      ];
      const measured = Math.max(...candidates.filter((n) => Number.isFinite(n) && n > 0));
      if (measured > 0) {
        setHeight((prev) =>
          Math.abs(measured + 8 - prev) > 4 ? measured + 8 : prev,
        );
      }
    };

    const setup = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      // Reset CSS pra evitar over-sizing: muitos templates usam
      // `min-height: 100vh` ou `height: 100%` no body — isso faz o body
      // ocupar toda a altura do iframe, e quando medimos scrollHeight ele
      // cresce indefinidamente. Forçamos `auto` pra que o body sigan o
      // tamanho real do conteúdo.
      try {
        const reset = doc.createElement("style");
        reset.setAttribute("data-editor-reset", "1");
        reset.textContent = "html, body { min-height: auto !important; height: auto !important; }";
        // Só adiciona uma vez (se srcDoc resetar, o head é novo).
        if (!doc.querySelector("style[data-editor-reset]")) {
          (doc.head ?? doc.documentElement).appendChild(reset);
        }
      } catch (err) {
        console.debug("[CustomHtmlIframe] não conseguiu injetar reset CSS:", err);
      }

      // Habilita edição WYSIWYG — clique em qualquer texto e edita inline.
      if (editable) {
        try { doc.designMode = "on"; } catch (err) {
          console.debug("[CustomHtmlIframe] designMode falhou:", err);
        }

        // Sem isso, clique em <a> dentro de designMode tenta navegar (o
        // sandbox bloqueia o navigate em si, mas o cursor não chega a ser
        // posicionado — usuário acha que "botão não funciona"). Prevenimos
        // o default no capture phase pra liberar o cursor sem atrapalhar
        // designMode.
        const blockLinkNav = (e: Event) => {
          let el = e.target as HTMLElement | null;
          while (el && el !== doc.body && el !== doc.documentElement) {
            if (el.tagName === "A") {
              e.preventDefault();
              return;
            }
            el = el.parentElement;
          }
        };
        doc.addEventListener("click", blockLinkNav, true);
        // Cleanup junto com o resto (anexado no input listener cleanup).
        linkNavBlocker = blockLinkNav;
      }

      measure();

      if (typeof ResizeObserver !== "undefined") {
        ro?.disconnect();
        ro = new ResizeObserver(() => measure());
        ro.observe(doc.documentElement);
        if (doc.body) ro.observe(doc.body);
      }

      doc.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          const fn = () => measure();
          img.addEventListener("load", fn);
          img.addEventListener("error", fn);
          imageListeners.push({ img, fn });
        }
      });

      // ResizeObserver já cobre 99% dos relayouts. Mantemos dois polls como
      // belt-and-suspenders pra navegadores/templates que não disparam o
      // observer no primeiro paint (webfonts antigas, content-visibility, etc.).
      pollTimeouts = [600, 2500].map((d) => setTimeout(measure, d));

      // Captura edições do usuário e propaga pro parent state (debounced).
      // Lê o callback via ref pra não depender da identidade da função
      // (que pode mudar a cada render do parent se não memoizada).
      if (editable) {
        // flushNow: serializa o DOM atual e propaga IMEDIATAMENTE (cancela
        // debounce pendente). Usado pelo parent antes de salvar, e pelo
        // próprio iframe quando o foco sai (`focusout`). Retorna o HTML
        // serializado pra que o caller possa usar sincronamente sem esperar
        // o React processar o setState.
        const flushNow = (): string | null => {
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
          }
          if (!doc.documentElement) return null;
          const serialized = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
          // Só emite se mudou (evita re-render desnecessário no parent).
          if (serialized !== lastEmittedRef.current) {
            lastEmittedRef.current = serialized;
            onHtmlChangeRef.current?.(serialized);
          }
          return serialized;
        };

        inputHandler = () => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          // 150ms (era 400ms): rápido o bastante pra cobrir clique imediato
          // em "Salvar"/"Enviar" depois da última tecla. O parent ainda
          // chama flushNow() explicitamente, então debounce é só pra reduzir
          // a frequência de setState durante digitação rápida.
          debounceRef.current = setTimeout(() => {
            const serialized = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
            if (serialized !== lastEmittedRef.current) {
              lastEmittedRef.current = serialized;
              onHtmlChangeRef.current?.(serialized);
            }
          }, 150);
        };
        doc.addEventListener("input", inputHandler);

        // focusout: usuário tirou foco do iframe (clicou em qualquer outro
        // elemento da página, incluindo "Salvar"/"Enviar"). Flush sincrono
        // pra garantir que o save peque a versão mais recente.
        focusoutHandler = () => { flushNow(); };
        doc.addEventListener("focusout", focusoutHandler);

        // Registra o flush no parent — o handleSaveAndAdvance chama isso
        // antes de mutar/navegar pra eliminar a corrida com o debounce.
        registeredFlushFn = flushNow;
        onRegisterFlushRef.current?.(flushNow);
      }
    };

    if (iframe.contentDocument?.readyState === "complete") {
      setup();
    }
    iframe.addEventListener("load", setup);

    return () => {
      iframe.removeEventListener("load", setup);
      ro?.disconnect();
      imageListeners.forEach(({ img, fn }) => {
        img.removeEventListener("load", fn);
        img.removeEventListener("error", fn);
      });
      pollTimeouts.forEach(clearTimeout);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (inputHandler) {
        try { iframe.contentDocument?.removeEventListener("input", inputHandler); } catch (err) {
          console.debug("[CustomHtmlIframe] removeEventListener(input) falhou:", err);
        }
      }
      if (focusoutHandler) {
        try { iframe.contentDocument?.removeEventListener("focusout", focusoutHandler); } catch (err) {
          console.debug("[CustomHtmlIframe] removeEventListener(focusout) falhou:", err);
        }
      }
      if (linkNavBlocker) {
        try { iframe.contentDocument?.removeEventListener("click", linkNavBlocker, true); } catch (err) {
          console.debug("[CustomHtmlIframe] removeEventListener(click) falhou:", err);
        }
      }
      // Desregistra do parent — quem pegou nosso flush não pode mais chamá-lo.
      if (registeredFlushFn) {
        onRegisterFlushRef.current?.(null);
      }
    };
    // `editable` é booleano estável; `srcDoc` muda só em mudança externa.
    // `onHtmlChange` removido da dep porque é lido via ref (ver acima).
  }, [editable, srcDoc]);

  return (
    <div className="relative w-full">
      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        title={editable ? "Editor do e-mail (clique para editar)" : "Pré-visualização do e-mail"}
        className="w-full block border-0"
        style={{ height, minHeight: 240 }}
      />
      {/* Overlay de seleção: quando o bloco não está selecionado, captura
          o primeiro clique pra selecionar (abrir painel de propriedades).
          Quando selecionado, o overlay some e os cliques vão pro iframe
          (designMode permite editar inline). */}
      {!isSelected && (
        <div
          role="button"
          aria-label="Selecionar bloco para editar"
          className="absolute inset-0 cursor-pointer bg-transparent"
          onClick={(e) => { e.stopPropagation(); onActivate?.(); }}
        />
      )}
      {isSelected && editable && (
        <div className="absolute top-2 left-2 text-[10px] font-medium bg-emerald-100 text-emerald-900 px-2 py-1 rounded shadow-sm pointer-events-none">
          Clique em qualquer texto para editar
        </div>
      )}
    </div>
  );
}

// ─── Toolbar flutuante de formatação ────────────────────────────────────────
//
// Aparece logo acima do bloco quando um contentEditable interno está em foco.
// Tem duas seções:
//   • Inline (selection-level): B / I / Link — agem na seleção via execCommand.
//     Ficam desabilitados quando não há seleção textual (ex.: cursor sozinho).
//   • Block-level: alinhamento, tamanho da fonte, cor do texto — só aparecem
//     pra blocos do tipo `text` (header e button têm campos próprios no painel
//     direito). Editam direto `block.align`, `block.fontSize`, `block.textColor`.
//
// Cmd/Ctrl+B e Cmd/Ctrl+I do navegador continuam funcionando paralelamente.

function InlineFormatToolbar({
  blocks,
  onUpdateBlock,
}: {
  blocks: Block[];
  onUpdateBlock: (id: string, updates: Partial<Block>) => void;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const savedRangeRef = useRef<Range | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const activeBlock = activeBlockId ? blocks.find((b) => b.id === activeBlockId) ?? null : null;

  useEffect(() => {
    function update() {
      const sel = window.getSelection();
      const focused = document.activeElement as HTMLElement | null;
      // Se o foco está em algum botão/input da toolbar, não muda nada.
      if (focused?.closest("[data-format-toolbar]")) return;
      // Se o foco está dentro de um popover/dropdown do Radix (portalizado fora
      // do toolbar), também ignora — mantém toolbar visível enquanto o picker
      // de variáveis ou outro menu está aberto.
      if (focused?.closest("[data-radix-popper-content-wrapper]") || focused?.closest("[role=menu]")) return;
      const editable = focused?.closest("[data-editable-text]") as HTMLElement | null;
      if (!editable) {
        if (linkOpen || pickerOpen) return;
        setPos(null);
        setActiveBlockId(null);
        return;
      }
      const blockId = editable.getAttribute("data-block-id");
      if (blockId) setActiveBlockId(blockId);

      // Salva o range ativo (pra restaurar antes de execCommand).
      const nonEmptySel = !!sel && !sel.isCollapsed && sel.rangeCount > 0;
      setHasSelection(nonEmptySel);
      if (nonEmptySel) savedRangeRef.current = sel!.getRangeAt(0).cloneRange();

      // Posiciona acima do bloco do canvas (não da seleção) — fica estável
      // mesmo quando o usuário só posiciona o cursor sem selecionar texto.
      const blockEl = editable.closest("[data-canvas-block]") as HTMLElement | null;
      const rect = (blockEl ?? editable).getBoundingClientRect();
      setPos({ x: rect.left + rect.width / 2, y: rect.top });
    }

    function onFocusOut() {
      // Esperar o foco mover (pode ir pra um botão da própria toolbar).
      setTimeout(update, 80);
    }

    document.addEventListener("selectionchange", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("selectionchange", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [linkOpen, pickerOpen]);

  function restoreSelection() {
    const sel = window.getSelection();
    const range = savedRangeRef.current;
    if (sel && range) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  function applyExec(cmd: "bold" | "italic") {
    restoreSelection();
    document.execCommand(cmd, false);
  }

  function applyLink() {
    restoreSelection();
    const u = linkUrl.trim();
    if (u) document.execCommand("createLink", false, u);
    setLinkOpen(false);
    setLinkUrl("");
  }

  if (!pos || !activeBlock) return null;

  const isText = activeBlock.type === "text";
  const TOOLBAR_HEIGHT = 44;
  const align = activeBlock.align ?? "left";
  const fontSize = activeBlock.fontSize ?? 15;
  const textColor = activeBlock.textColor ?? "#374151";

  // Recolhe do viewport: garante que a toolbar fique visível mesmo se o bloco
  // está colado no topo da página.
  const top = Math.max(8, pos.y - TOOLBAR_HEIGHT);
  const left = Math.max(8, Math.min(pos.x - 200, window.innerWidth - 420));

  return (
    <div
      ref={toolbarRef}
      data-format-toolbar
      role="toolbar"
      aria-label="Formatar bloco"
      className="fixed z-50 flex items-center gap-0.5 rounded-md border border-border bg-background shadow-lg px-1 py-1"
      style={{ left, top }}
      onMouseDown={(e) => {
        // Preserva o foco do contentEditable (e a seleção pra execCommand)
        // — só evita o blur se o alvo não for um input editável da própria
        // toolbar (cor/tamanho/url).
        const t = e.target as HTMLElement;
        if (!t.closest("input")) e.preventDefault();
      }}
    >
      {/* Inline: negrito / itálico / link */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={!hasSelection}
        onClick={() => applyExec("bold")}
        title="Negrito (Cmd/Ctrl+B)"
      >
        <Bold className="w-3.5 h-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={!hasSelection}
        onClick={() => applyExec("italic")}
        title="Itálico (Cmd/Ctrl+I)"
      >
        <Italic className="w-3.5 h-3.5" />
      </Button>
      {linkOpen ? (
        <div className="flex items-center gap-1 pl-1 ml-1 border-l">
          <input
            autoFocus
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
              if (e.key === "Escape") {
                setLinkOpen(false);
                setLinkUrl("");
              }
            }}
            placeholder="https://..."
            className="text-xs px-2 py-1 rounded border bg-background w-40 outline-none focus:ring-1 focus:ring-primary"
          />
          <Button size="sm" className="h-7 text-xs px-2" onClick={applyLink}>
            OK
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={!hasSelection}
          onClick={() => setLinkOpen(true)}
          title={hasSelection ? "Adicionar link na seleção" : "Selecione um trecho para adicionar link"}
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </Button>
      )}

      <div className="mx-1 h-5 w-px bg-border" />

      {/* Inserir variável: insere o token na posição do cursor via execCommand.
          O dropdown do Radix portaliza fora do toolbar — por isso usamos open
          controlado e a flag `pickerOpen` mantém o toolbar visível enquanto o
          menu está aberto (caso contrário o focusout esconderia tudo). */}
      <VariablePicker
        align="start"
        size="sm"
        variant="ghost"
        buttonClassName="h-7 px-2"
        open={pickerOpen}
        onOpenChange={(o) => {
          // Salva range ANTES de abrir, pq o focus muda pra o trigger e o range
          // existente ainda é válido. Ao fechar, restauramos o foco.
          if (o) {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
              savedRangeRef.current = sel.getRangeAt(0).cloneRange();
            }
          }
          setPickerOpen(o);
        }}
        onInsert={(token) => {
          setPickerOpen(false);
          // Devolve o foco pro contentEditable salvo antes de inserir.
          const r = savedRangeRef.current;
          if (r) {
            const startContainer = r.startContainer;
            const editable = (startContainer.nodeType === 1
              ? (startContainer as Element)
              : startContainer.parentElement
            )?.closest("[data-editable-text]") as HTMLElement | null;
            editable?.focus();
            const sel = window.getSelection();
            if (sel) {
              sel.removeAllRanges();
              sel.addRange(r);
            }
          }
          document.execCommand("insertText", false, token);
        }}
      />

      {/* Controles em nível de bloco (só pra blocos `text`) */}
      {isText && (
        <>
          <div className="mx-1 h-5 w-px bg-border" />

          {/* Alinhamento */}
          {(["left", "center", "right"] as const).map((a) => (
            <Button
              key={a}
              variant={align === a ? "default" : "ghost"}
              size="icon"
              className="h-7 w-7"
              onClick={() => onUpdateBlock(activeBlock.id, { align: a })}
              title={a === "left" ? "Alinhar à esquerda" : a === "center" ? "Centralizar" : "Alinhar à direita"}
            >
              {a === "left" ? (
                <AlignLeft className="w-3.5 h-3.5" />
              ) : a === "center" ? (
                <AlignCenter className="w-3.5 h-3.5" />
              ) : (
                <AlignRight className="w-3.5 h-3.5" />
              )}
            </Button>
          ))}

          <div className="mx-1 h-5 w-px bg-border" />

          {/* Tamanho da fonte */}
          <div className="flex items-center gap-1 pl-1 pr-1" title="Tamanho da fonte (px)">
            <span className="text-[10px] text-muted-foreground font-medium">A</span>
            <input
              type="number"
              min={10}
              max={64}
              value={fontSize}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) onUpdateBlock(activeBlock.id, { fontSize: Math.max(10, Math.min(64, n)) });
              }}
              className="w-12 h-7 text-xs px-1 rounded border bg-background outline-none focus:ring-1 focus:ring-primary text-center"
            />
          </div>

          <div className="mx-1 h-5 w-px bg-border" />

          {/* Cor do texto */}
          <label
            className="flex items-center gap-1 pl-1 pr-1 cursor-pointer"
            title="Cor do texto"
          >
            <span className="text-[10px] text-muted-foreground font-medium">Cor</span>
            <input
              type="color"
              value={textColor}
              onChange={(e) => onUpdateBlock(activeBlock.id, { textColor: e.target.value })}
              className="w-6 h-6 rounded border cursor-pointer p-0"
            />
          </label>
        </>
      )}
    </div>
  );
}

// ─── Bloco "sortable" no canvas + drop-zones do painel ──────────────────────

function SortableBlock({
  block,
  isSelected,
  onSelect,
  children,
  controls,
}: {
  block: Block;
  isSelected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  controls: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style: React.CSSProperties = {
    transform: DndCSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      data-canvas-block={block.id}
      style={style}
      className={`relative group cursor-pointer transition-all ${
        isSelected ? "ring-2 ring-primary ring-offset-1" : "hover:ring-1 hover:ring-primary/40"
      }`}
      onClick={onSelect}
    >
      {children}
      {/* Handle de arrasto: aparece no hover/selected, no canto esquerdo do bloco. */}
      <button
        type="button"
        aria-label="Arrastar para reordenar"
        title="Arrastar"
        className={`absolute top-1 left-1 p-1 bg-background/90 border rounded shadow-sm cursor-grab active:cursor-grabbing transition-opacity ${
          isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3 h-3 text-muted-foreground" />
      </button>
      {controls}
    </div>
  );
}

// Slot fino entre dois blocos no canvas. Vira drop-target visível só quando
// um bloco-de-painel está sendo arrastado.
function CanvasDropZone({ index, active }: { index: number; active: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: `dropzone:${index}` });
  return (
    <div
      ref={setNodeRef}
      style={{ height: active ? 24 : 4 }}
      className={`transition-all ${
        active
          ? isOver
            ? "bg-primary/30 border-y-2 border-primary"
            : "bg-primary/5 border-y border-dashed border-primary/40"
          : ""
      }`}
    />
  );
}

// Botão de bloco no painel esquerdo, agora arrastável pra dentro do canvas.
function PanelBlockButton({
  type,
  Icon,
  label,
  onAdd,
}: {
  type: BlockType;
  Icon: any;
  label: string;
  onAdd: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `panel:${type}` });
  return (
    <button
      ref={setNodeRef}
      onClick={onAdd}
      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer text-center ${
        isDragging ? "opacity-50" : ""
      }`}
      title={`Clique para adicionar ou arraste para o canvas`}
      {...attributes}
      {...listeners}
    >
      <Icon className="w-5 h-5 text-muted-foreground" />
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

// ─── Editor do bloco Imagem (com upload) ─────────────────────────────────────

function ImageBlockEditor({ block, onChange }: { block: Block; onChange: (u: Partial<Block>) => void }) {
  const uploadMutation = trpc.upload.image.useMutation({
    onSuccess: (data) => {
      onChange({ imageUrl: data.url });
      toast.success("Imagem enviada!");
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro no upload")),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxMb = 5;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Imagem muito grande. Máximo ${maxMb}MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      uploadMutation.mutate({ base64, fileName: file.name, mimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Upload de Imagem</Label>
        <label className="mt-1 flex items-center gap-2 cursor-pointer">
          <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs" disabled={uploadMutation.isPending} asChild>
            <span>
              <Upload className="w-3.5 h-3.5" />
              {uploadMutation.isPending ? "Enviando..." : "Escolher arquivo"}
            </span>
          </Button>
          <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          <span className="text-xs text-muted-foreground">PNG, JPG, GIF até 5MB</span>
        </label>
      </div>
      <div>
        <Label className="text-xs">ou cole a URL da imagem</Label>
        <Input value={block.imageUrl ?? ""} onChange={e => onChange({ imageUrl: e.target.value })} className="mt-1 h-8 text-sm" placeholder="https://..." />
      </div>
      {block.imageUrl && (
        <div className="rounded border overflow-hidden">
          <img src={block.imageUrl} alt="preview" className="max-h-28 w-full object-contain bg-muted" />
        </div>
      )}
      <div>
        <Label className="text-xs">Texto alternativo</Label>
        <Input value={block.imageAlt ?? ""} onChange={e => onChange({ imageAlt: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Descrição da imagem" />
      </div>
      <div>
        <Label className="text-xs">Link ao clicar (opcional)</Label>
        <Input value={block.imageLink ?? ""} onChange={e => onChange({ imageLink: e.target.value })} className="mt-1 h-8 text-sm" placeholder="https://..." />
      </div>
      <div>
        <Label className="text-xs">Largura da imagem (%)</Label>
        <Input
          type="number" min={10} max={100}
          value={block.imageWidth ?? 100}
          onChange={e => onChange({ imageWidth: Number(e.target.value) })}
          className="mt-1 h-8 text-sm w-24"
        />
      </div>
    </div>
  );
}

// ─── Editor do bloco customHtml ──────────────────────────────────────────────
//
// HTML importado é tratado como um bloco único: tags individuais (botões,
// parágrafos) não viram blocos visuais. Pra dar uma porta de entrada de
// edição rápida sem o usuário mergulhar no código, expomos:
//  1. Find & replace de texto literal — troca rótulo de botão, texto do hero,
//     links, etc., sem editar o HTML manualmente.
//  2. Editor de código completo (textarea) — para ajustes finos.

function CustomHtmlEditorPanel({
  block,
  onChange,
}: {
  block: Block;
  onChange: (updated: Partial<Block>) => void;
}) {
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [lastReplace, setLastReplace] = useState<{ count: number } | null>(null);

  const doReplace = () => {
    if (!findText) return;
    const current = block.customHtml ?? "";
    // Replace literal (não regex) para evitar surpresa do usuário com
    // metacaracteres. `split + join` faz substituição global de string fixa.
    const next = current.split(findText).join(replaceText);
    const count = current.split(findText).length - 1;
    if (count > 0) {
      onChange({ customHtml: next });
      setLastReplace({ count });
    } else {
      setLastReplace({ count: 0 });
    }
  };

  // Extrai todos os <a> do HTML para listar e permitir edição de URL.
  // DOMParser é a forma mais robusta de parsear HTML arbitrário no client.
  // O nth (index em document order) é a chave estável usada para atualizar
  // o link correto na re-serialização.
  const links = useMemo(() => {
    const html = block.customHtml ?? "";
    if (!html) return [] as { index: number; text: string; href: string }[];
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const anchors = Array.from(doc.querySelectorAll("a"));
      return anchors
        .map((a, index) => ({
          index,
          text: (a.textContent ?? "").trim().slice(0, 80),
          href: a.getAttribute("href") ?? "",
        }))
        // Filtra âncoras invisíveis (sem texto E sem href interessante).
        .filter((l) => l.text || (l.href && l.href !== "#"));
    } catch {
      return [];
    }
  }, [block.customHtml]);

  const updateLinkHref = (targetIndex: number, newHref: string) => {
    const html = block.customHtml ?? "";
    if (!html) return;
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const anchors = doc.querySelectorAll("a");
      const target = anchors[targetIndex];
      if (!target) return;
      target.setAttribute("href", newHref);
      // Serializa de volta. doctype é preservado pelo parser; reconstruímos
      // explicitamente pra garantir.
      const serialized = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
      onChange({ customHtml: serialized });
    } catch {
      // ignore: HTML mal-formado, fica como está
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-900 leading-relaxed">
        <strong className="font-semibold">Modelo HTML pronto.</strong> Edição inline: clique em qualquer texto/botão no canvas pra alterar. Use as seções abaixo pra trocar URLs de botões/links e fazer buscas globais.
      </div>

      {links.length > 0 && (
        <div className="rounded-md border border-input p-2.5 space-y-2 bg-muted/30">
          <Label className="text-xs font-semibold">Links e Botões ({links.length})</Label>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Edite o URL pra onde cada botão/link aponta. O texto edita-se clicando direto no canvas.
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {links.map((link) => (
              <LinkEditorRow
                key={link.index}
                link={link}
                onUpdateHref={(newHref) => updateLinkHref(link.index, newHref)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="rounded-md border border-input p-2.5 space-y-2 bg-muted/30">
        <Label className="text-xs font-semibold">Substituir texto</Label>
        <Input
          value={findText}
          onChange={(e) => setFindText(e.target.value)}
          placeholder='Texto antigo (ex: "Saiba mais")'
          className="h-8 text-xs"
        />
        <Input
          value={replaceText}
          onChange={(e) => setReplaceText(e.target.value)}
          placeholder='Texto novo (ex: "Inscreva-se")'
          className="h-8 text-xs"
        />
        <Button
          size="sm"
          variant="secondary"
          onClick={doReplace}
          disabled={!findText}
          className="w-full h-7 text-xs gap-1.5"
        >
          Substituir todas
        </Button>
        {lastReplace && (
          <p className={`text-[11px] ${lastReplace.count > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>
            {lastReplace.count > 0
              ? `${lastReplace.count} ocorrência(s) substituída(s).`
              : "Nenhuma ocorrência encontrada."}
          </p>
        )}
      </div>

      <div>
        <Label className="text-xs">Código HTML</Label>
        {/*
          <textarea> nativo em vez do shadcn Textarea: o componente padrão
          usa `field-sizing-content` que cresce com o conteúdo e estoura
          a largura do painel de 300px quando o HTML tem linhas longas.
          Aqui forçamos largura constrita e scroll interno (X e Y).
        */}
        <textarea
          value={block.customHtml ?? ""}
          onChange={e => onChange({ customHtml: e.target.value })}
          spellCheck={false}
          wrap="off"
          className="mt-1 w-full h-72 max-w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-[11px] font-mono leading-relaxed shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] resize-none overflow-auto whitespace-pre"
        />
      </div>
    </div>
  );
}

// Linha editável de um link. Usa local state pro input pra não dar lag a
// cada keystroke (a serialização do HTML é cara). Commit no blur ou Enter.
// Inclui botão "abrir em nova aba" pra testar o destino do link sem precisar
// disparar e-mail real.
function LinkEditorRow({
  link,
  onUpdateHref,
}: {
  link: { index: number; text: string; href: string };
  onUpdateHref: (next: string) => void;
}) {
  const [draft, setDraft] = useState(link.href);
  useEffect(() => { setDraft(link.href); }, [link.href]);

  const commit = () => {
    if (draft !== link.href) onUpdateHref(draft);
  };

  // Só consideramos "testável" URLs http(s). `#`, `mailto:`, vazio etc.
  // ficam desabilitados (o anchor `target="_blank"` precisa de http(s) válido).
  const isTestable = /^https?:\/\//i.test(draft.trim());

  return (
    <div className="rounded border bg-background p-2 space-y-1">
      <div className="text-[11px] text-muted-foreground truncate" title={link.text}>
        {link.text || <em className="text-muted-foreground/70">(sem texto)</em>}
      </div>
      <div className="flex gap-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); (e.target as HTMLInputElement).blur(); } }}
          placeholder="https://"
          className="h-7 text-[11px] font-mono flex-1 min-w-0"
          spellCheck={false}
        />
        <a
          href={isTestable ? draft : undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!isTestable}
          title={isTestable ? "Abrir em nova aba" : "URL inválida para testar"}
          className={`shrink-0 inline-flex items-center justify-center h-7 w-7 rounded border ${
            isTestable
              ? "border-input bg-background hover:bg-muted cursor-pointer"
              : "border-input bg-muted/40 text-muted-foreground/60 pointer-events-none"
          }`}
          onClick={(e) => { if (!isTestable) e.preventDefault(); }}
        >
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}

// ─── Editor de Bloco (Propriedades) ──────────────────────────────────────────

function BlockEditor({ block, onChange }: { block: Block; onChange: (updated: Partial<Block>) => void }) {
  const titleRef = useRef<HTMLInputElement>(null);
  const subtitleRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const btnTextRef = useRef<HTMLInputElement>(null);
  const listItemsRef = useRef<HTMLTextAreaElement>(null);

  switch (block.type) {
    case "header":
      return (
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Título</Label>
              <VariablePicker
                align="end"
                iconOnly
                onInsert={(t) => insertAtCursor(titleRef.current, block.title ?? "", t, (v) => onChange({ title: v }))}
              />
            </div>
            <Input ref={titleRef} value={block.title ?? ""} onChange={e => onChange({ title: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Título do cabeçalho" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Subtítulo (opcional)</Label>
              <VariablePicker
                align="end"
                iconOnly
                onInsert={(t) => insertAtCursor(subtitleRef.current, block.subtitle ?? "", t, (v) => onChange({ subtitle: v }))}
              />
            </div>
            <Input ref={subtitleRef} value={block.subtitle ?? ""} onChange={e => onChange({ subtitle: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Subtítulo" />
          </div>
          <div>
            <Label className="text-xs">URL do Logo (opcional)</Label>
            <Input value={block.headerLogoUrl ?? ""} onChange={e => onChange({ headerLogoUrl: e.target.value })} className="mt-1 h-8 text-sm" placeholder="https://..." />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Cor de fundo</Label>
              <div className="flex gap-1.5 mt-1">
                <input type="color" value={block.headerBg ?? "#2563eb"} onChange={e => onChange({ headerBg: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
                <Input value={block.headerBg ?? "#2563eb"} onChange={e => onChange({ headerBg: e.target.value })} className="h-8 text-xs font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Cor do texto</Label>
              <div className="flex gap-1.5 mt-1">
                <input type="color" value={block.headerTextColor ?? "#ffffff"} onChange={e => onChange({ headerTextColor: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
                <Input value={block.headerTextColor ?? "#ffffff"} onChange={e => onChange({ headerTextColor: e.target.value })} className="h-8 text-xs font-mono" />
              </div>
            </div>
          </div>
        </div>
      );

    case "text":
      return (
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Label className="text-xs">Texto</Label>
              <div className="ml-auto">
                <VariablePicker
                  align="end"
                  size="sm"
                  variant="ghost"
                  buttonClassName="h-6 px-1.5 text-xs"
                  onInsert={(t) => insertAtCursor(textRef.current, block.text ?? "", t, (v) => onChange({ text: v }))}
                />
              </div>
            </div>
            <Textarea ref={textRef} value={block.text ?? ""} onChange={e => onChange({ text: e.target.value })} rows={5} className="text-sm" placeholder="Escreva o conteúdo..." />
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
              Dica: <code className="px-1 rounded bg-muted">**negrito**</code> ·{" "}
              <code className="px-1 rounded bg-muted">*itálico*</code> ·{" "}
              <code className="px-1 rounded bg-muted">[texto](url)</code>{" "}
              · pressione Enter para nova linha
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tamanho da fonte (px)</Label>
              <Input type="number" min={10} max={64} value={block.fontSize ?? 15} onChange={e => onChange({ fontSize: Number(e.target.value) })} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Cor do texto</Label>
              <div className="flex gap-1.5 mt-1">
                <input type="color" value={block.textColor ?? "#374151"} onChange={e => onChange({ textColor: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
                <Input value={block.textColor ?? "#374151"} onChange={e => onChange({ textColor: e.target.value })} className="h-8 text-xs font-mono" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Alinhamento</Label>
            <div className="flex gap-1 mt-1">
              {(["left", "center", "right"] as const).map(align => (
                <Button key={align} variant={block.align === align ? "default" : "outline"} size="sm" className="h-7 px-2" onClick={() => onChange({ align })}>
                  {align === "left" ? <AlignLeft className="w-3.5 h-3.5" /> : align === "center" ? <AlignCenter className="w-3.5 h-3.5" /> : <AlignRight className="w-3.5 h-3.5" />}
                </Button>
              ))}
            </div>
          </div>
          {/* "Card" styling — colored background + border + padding. Used by
              templates de cupom, código de rastreio, etc. Aparece colapsado por
              padrão; expande quando o bloco já tem alguma estilização aplicada. */}
          <details className="border-t pt-2 -mx-1 px-1" open={!!(block.bgColor || block.borderColor)}>
            <summary className="text-xs font-medium cursor-pointer select-none text-muted-foreground hover:text-foreground">
              Estilo de card (fundo, borda)
            </summary>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Cor de fundo</Label>
                  <div className="flex gap-1.5 mt-1">
                    <input type="color" value={block.bgColor ?? "#ffffff"} onChange={e => onChange({ bgColor: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
                    <Input value={block.bgColor ?? ""} onChange={e => onChange({ bgColor: e.target.value || undefined })} placeholder="(nenhum)" className="h-8 text-xs font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Cor da borda</Label>
                  <div className="flex gap-1.5 mt-1">
                    <input type="color" value={block.borderColor ?? "#e5e7eb"} onChange={e => onChange({ borderColor: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
                    <Input value={block.borderColor ?? ""} onChange={e => onChange({ borderColor: e.target.value || undefined })} placeholder="(nenhuma)" className="h-8 text-xs font-mono" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Estilo borda</Label>
                  <Select value={block.borderStyle ?? "solid"} onValueChange={(v) => onChange({ borderStyle: v as "solid" | "dashed" | "dotted" })}>
                    <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid" className="text-xs">Sólida</SelectItem>
                      <SelectItem value="dashed" className="text-xs">Tracejada</SelectItem>
                      <SelectItem value="dotted" className="text-xs">Pontilhada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Espessura</Label>
                  <Input type="number" min={0} max={8} value={block.borderWidth ?? 1} onChange={e => onChange({ borderWidth: Number(e.target.value) })} className="mt-1 h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Arredondar</Label>
                  <Input type="number" min={0} max={32} value={block.borderRadius ?? 8} onChange={e => onChange({ borderRadius: Number(e.target.value) })} className="mt-1 h-8 text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Padding interno (px)</Label>
                <Input type="number" min={0} max={64} value={block.padding ?? 24} onChange={e => onChange({ padding: Number(e.target.value) })} className="mt-1 h-8 text-sm w-20" />
              </div>
              {(block.bgColor || block.borderColor) && (
                <Button variant="ghost" size="sm" className="w-full text-xs h-7" onClick={() => onChange({ bgColor: undefined, borderColor: undefined, borderStyle: undefined, borderWidth: undefined, borderRadius: undefined, padding: undefined })}>
                  Remover estilo de card
                </Button>
              )}
            </div>
          </details>
        </div>
      );

    case "image":
      return <ImageBlockEditor block={block} onChange={onChange} />;

    case "button":
      return (
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Texto do botão</Label>
              <VariablePicker
                align="end"
                iconOnly
                onInsert={(t) => insertAtCursor(btnTextRef.current, block.btnText ?? "", t, (v) => onChange({ btnText: v }))}
              />
            </div>
            <Input ref={btnTextRef} value={block.btnText ?? ""} onChange={e => onChange({ btnText: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Saiba mais" />
          </div>
          <div>
            <Label className="text-xs">URL de destino</Label>
            <Input value={block.btnUrl ?? ""} onChange={e => onChange({ btnUrl: e.target.value })} className="mt-1 h-8 text-sm" placeholder="https://..." />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Cor do botão</Label>
              <div className="flex gap-1.5 mt-1">
                <input type="color" value={block.btnBg ?? "#2563eb"} onChange={e => onChange({ btnBg: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
                <Input value={block.btnBg ?? "#2563eb"} onChange={e => onChange({ btnBg: e.target.value })} className="h-8 text-xs font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Cor do texto</Label>
              <div className="flex gap-1.5 mt-1">
                <input type="color" value={block.btnTextColor ?? "#ffffff"} onChange={e => onChange({ btnTextColor: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
                <Input value={block.btnTextColor ?? "#ffffff"} onChange={e => onChange({ btnTextColor: e.target.value })} className="h-8 text-xs font-mono" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Arredondamento (px)</Label>
            <Input type="number" min={0} max={50} value={block.btnBorderRadius ?? 6} onChange={e => onChange({ btnBorderRadius: Number(e.target.value) })} className="mt-1 h-8 text-sm w-20" />
          </div>
          <div>
            <Label className="text-xs">Alinhamento</Label>
            <div className="flex gap-1 mt-1">
              {(["left", "center", "right"] as const).map(align => (
                <Button key={align} variant={block.btnAlign === align ? "default" : "outline"} size="sm" className="h-7 px-3 text-xs" onClick={() => onChange({ btnAlign: align })}>
                  {align === "left" ? "Esq" : align === "center" ? "Centro" : "Dir"}
                </Button>
              ))}
            </div>
          </div>
        </div>
      );

    case "divider":
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Cor do divisor</Label>
            <div className="flex gap-1.5 mt-1">
              <input type="color" value={block.dividerColor ?? "#e5e7eb"} onChange={e => onChange({ dividerColor: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" />
              <Input value={block.dividerColor ?? "#e5e7eb"} onChange={e => onChange({ dividerColor: e.target.value })} className="h-8 text-xs font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Estilo</Label>
            <div className="flex gap-1 mt-1">
              {(["solid", "dashed", "dotted"] as const).map(s => (
                <Button key={s} variant={block.dividerStyle === s ? "default" : "outline"} size="sm" className="h-7 px-3 text-xs" onClick={() => onChange({ dividerStyle: s })}>
                  {s === "solid" ? "Sólido" : s === "dashed" ? "Tracejado" : "Pontilhado"}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Espessura (px)</Label>
            <Input type="number" min={1} max={10} value={block.dividerThickness ?? 1} onChange={e => onChange({ dividerThickness: Number(e.target.value) })} className="mt-1 h-8 text-sm w-20" />
          </div>
          <div>
            <Label className="text-xs">Largura (%)</Label>
            <Input type="number" min={10} max={100} value={block.dividerWidth ?? 100} onChange={e => onChange({ dividerWidth: Number(e.target.value) })} className="mt-1 h-8 text-sm w-20" />
          </div>
        </div>
      );

    case "spacer":
      return (
        <div>
          <Label className="text-xs">Altura (px)</Label>
          <Input type="number" value={block.spacerHeight ?? 24} onChange={e => onChange({ spacerHeight: Number(e.target.value) })} className="mt-1 h-8 text-sm w-24" min={8} max={120} />
        </div>
      );

    case "video":
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">URL do Vídeo (YouTube ou Vimeo)</Label>
            <Input value={block.videoUrl ?? ""} onChange={e => onChange({ videoUrl: e.target.value })} className="mt-1 h-8 text-sm" placeholder="https://youtube.com/watch?v=..." />
            <p className="text-xs text-muted-foreground mt-1">Para YouTube, a miniatura será extraída automaticamente.</p>
          </div>
          <div>
            <Label className="text-xs">Legenda (opcional)</Label>
            <Input value={block.videoCaption ?? ""} onChange={e => onChange({ videoCaption: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Assista ao nosso vídeo" />
          </div>
        </div>
      );

    case "list":
      return (
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Itens da lista (um por linha)</Label>
              <VariablePicker
                align="end"
                size="sm"
                variant="ghost"
                buttonClassName="h-6 px-1.5 text-xs"
                onInsert={(t) => insertAtCursor(listItemsRef.current, block.listItems ?? "", t, (v) => onChange({ listItems: v }))}
              />
            </div>
            <Textarea ref={listItemsRef} value={block.listItems ?? ""} onChange={e => onChange({ listItems: e.target.value })} rows={5} className="mt-1 text-sm" placeholder={"Primeiro item\nSegundo item\nTerceiro item"} />
          </div>
          <div>
            <Label className="text-xs">Estilo da lista</Label>
            <div className="flex gap-1 mt-1">
              {(["bullet", "numbered"] as const).map(style => (
                <Button key={style} variant={block.listStyle === style ? "default" : "outline"} size="sm" className="h-7 px-3 text-xs" onClick={() => onChange({ listStyle: style })}>
                  {style === "bullet" ? "Marcadores" : "Numerada"}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Alinhamento</Label>
            <div className="flex gap-1 mt-1">
              {(["left", "center", "right"] as const).map(align => (
                <Button key={align} variant={block.align === align ? "default" : "outline"} size="sm" className="h-7 px-2" onClick={() => onChange({ align })}>
                  {align === "left" ? <AlignLeft className="w-3.5 h-3.5" /> : align === "center" ? <AlignCenter className="w-3.5 h-3.5" /> : <AlignRight className="w-3.5 h-3.5" />}
                </Button>
              ))}
            </div>
          </div>
        </div>
      );

    case "columns":
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Coluna Esquerda — Texto</Label>
            <Textarea value={block.colLeftText ?? ""} onChange={e => onChange({ colLeftText: e.target.value })} rows={3} className="mt-1 text-sm" placeholder="Conteúdo da coluna esquerda" />
          </div>
          <div>
            <Label className="text-xs">Coluna Esquerda — URL da Imagem (opcional)</Label>
            <Input value={block.colLeftImageUrl ?? ""} onChange={e => onChange({ colLeftImageUrl: e.target.value })} className="mt-1 h-8 text-sm" placeholder="https://..." />
          </div>
          <Separator />
          <div>
            <Label className="text-xs">Coluna Direita — Texto</Label>
            <Textarea value={block.colRightText ?? ""} onChange={e => onChange({ colRightText: e.target.value })} rows={3} className="mt-1 text-sm" placeholder="Conteúdo da coluna direita" />
          </div>
          <div>
            <Label className="text-xs">Coluna Direita — URL da Imagem (opcional)</Label>
            <Input value={block.colRightImageUrl ?? ""} onChange={e => onChange({ colRightImageUrl: e.target.value })} className="mt-1 h-8 text-sm" placeholder="https://..." />
          </div>
        </div>
      );

    case "social": {
      const links = block.socialLinks ?? [];
      const platforms = ["instagram", "facebook", "twitter", "linkedin", "youtube", "tiktok", "whatsapp"];

      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Estilo dos ícones</Label>
            <div className="flex gap-1 mt-1">
              {(["colored", "dark", "light"] as const).map(s => (
                <Button key={s} variant={block.socialStyle === s ? "default" : "outline"} size="sm" className="h-7 px-3 text-xs" onClick={() => onChange({ socialStyle: s })}>
                  {s === "colored" ? "Colorido" : s === "dark" ? "Escuro" : "Claro"}
                </Button>
              ))}
            </div>
          </div>
          <Separator />
          {links.map((link, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs capitalize">{link.platform}</Label>
                <Input
                  value={link.url}
                  onChange={e => {
                    const newLinks = [...links];
                    newLinks[i] = { ...newLinks[i], url: e.target.value };
                    onChange({ socialLinks: newLinks });
                  }}
                  className="mt-1 h-8 text-sm"
                  placeholder={`https://${link.platform}.com/...`}
                />
              </div>
              <Button
                variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => onChange({ socialLinks: links.filter((_, j) => j !== i) })}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Select
            onValueChange={(platform) => {
              if (!links.find(l => l.platform === platform)) {
                onChange({ socialLinks: [...links, { platform, url: "" }] });
              }
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Adicionar rede social..." />
            </SelectTrigger>
            <SelectContent>
              {platforms.filter(p => !links.find(l => l.platform === p)).map(p => (
                <SelectItem key={p} value={p} className="text-xs capitalize">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    case "customHtml":
      return <CustomHtmlEditorPanel block={block} onChange={onChange} />;

    default:
      return null;
  }
}

// ─── Auto-save hook ──────────────────────────────────────────────────────────

function useAutoSave(enabled: boolean, dirty: boolean, save: () => void) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !dirty) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, 2000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [enabled, dirty, save]);
}

// ─── Página Principal: Editor Visual Fullscreen ──────────────────────────────

export default function Emails() {
  return (
    <MobileBlockerGate featureName="Editor Visual de E-mail">
      <EmailsImpl />
    </MobileBlockerGate>
  );
}

function EmailsImpl() {
  const [, navigate] = useLocation();

  // O editor atende dois "modos":
  //  - campaign: /emails/:id/editor → edita uma campanha (envio, segmentação, A/B, etc.)
  //  - template: /emails/templates/:id/edit → edita um template salvo (sem envio)
  // Quando templateId está setado, escondemos toda a UI específica de envio
  // (EmailDetailsBar, AttachToFlow, ABTestPanel, LeadCopy, botão Enviar) e
  // direcionamos salvar/autosave pro router de templates.
  const [, campaignParams] = useRoute("/emails/:id/editor");
  const [, templateParams] = useRoute("/emails/templates/:id/edit");
  const templateId = templateParams?.id ? parseInt(templateParams.id, 10) : undefined;
  const campaignId = !templateId && campaignParams?.id ? parseInt(campaignParams.id, 10) : undefined;
  const isTemplateMode = !!templateId;

  // Detecta ?ab=1 na URL pra abrir painel A/B
  const abFromUrl = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("ab") === "1";

  // State
  const [blocks, setBlocks] = useState<Block[]>([
    { id: nanoid(), type: "header", ...BLOCK_DEFAULTS.header },
    { id: nanoid(), type: "text", ...BLOCK_DEFAULTS.text },
    { id: nanoid(), type: "button", ...BLOCK_DEFAULTS.button },
    { id: nanoid(), type: "divider", ...BLOCK_DEFAULTS.divider },
  ]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [footer, setFooter] = useState(`© ${new Date().getFullYear()} ${COMPANY.legalEntity}. Todos os direitos reservados.`);
  const [design, setDesign] = useState<DesignSettings>(DEFAULT_DESIGN);
  const [emailName, setEmailName] = useState("Novo E-mail");
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [leftTab, setLeftTab] = useState<"content" | "rows" | "settings">("content");
  const [showStructure, setShowStructure] = useState(false);
  const [showLeadCopy, setShowLeadCopy] = useState(true);
  const [attachToFlowOpen, setAttachToFlowOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [segmentIds, setSegmentIds] = useState<number[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [importHtmlOpen, setImportHtmlOpen] = useState(false);

  // Queries
  const { data: campaign } = trpc.emailCampaigns.get.useQuery(
    { id: campaignId! },
    { enabled: !!campaignId }
  );

  const { data: templateDoc } = trpc.emailTemplates.get.useQuery(
    { id: templateId! },
    { enabled: !!templateId }
  );

  const updateCampaignMutation = trpc.emailCampaigns.update.useMutation({
    onSuccess: () => {
      setDirty(false);
      setLastSaved(new Date());
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao salvar")),
  });

  const updateTemplateMutation = trpc.emailTemplates.update.useMutation({
    onSuccess: () => {
      setDirty(false);
      setLastSaved(new Date());
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao salvar template")),
  });

  const saveTemplateMutation = trpc.emailTemplates.create.useMutation({
    onSuccess: () => {
      toast.success("Template salvo!");
      setShowSaveDialog(false);
      setTemplateName("");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const importToCampaignMutation = trpc.emailCampaigns.importHtml.useMutation({
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao importar HTML")),
  });
  const importToTemplateMutation = trpc.emailTemplates.importHtml.useMutation({
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao importar HTML")),
  });

  const utilsForInvalidation = trpc.useUtils();

  // Substitui o conteúdo atual pelo HTML importado. Server faz normalização,
  // re-hospeda imagens (data:URI e URLs externas) e sanitiza antes de gravar.
  // Após sucesso, recarrega o documento do server para popular `blocks` com
  // o customHtml processado.
  const handleImportHtmlInEditor = useCallback(async (html: string) => {
    type ImportResult = {
      rehostedImages: number;
      failedImages: number;
      compatWarnings?: string[];
    };
    const reportResult = (res: ImportResult) => {
      if (res.compatWarnings && res.compatWarnings.length > 0) {
        // Conteúdo importado não é compatível com e-mail (ex: SPA, página
        // que depende de JS). Toast persistente — usuário precisa ler.
        toast.error(res.compatWarnings[0], { duration: 15000 });
        return;
      }
      const detail = res.failedImages > 0
        ? `${res.rehostedImages} imagem(ns) re-hospedada(s), ${res.failedImages} falharam`
        : res.rehostedImages > 0
          ? `${res.rehostedImages} imagem(ns) re-hospedada(s)`
          : "HTML importado";
      toast.success(detail);
    };

    if (isTemplateMode && templateId) {
      const res = await importToTemplateMutation.mutateAsync({ templateId, html });
      reportResult(res);
      await utilsForInvalidation.emailTemplates.get.invalidate({ id: templateId });
    } else if (campaignId) {
      const res = await importToCampaignMutation.mutateAsync({ campaignId, html });
      reportResult(res);
      await utilsForInvalidation.emailCampaigns.get.invalidate({ id: campaignId });
    }
    setImportHtmlOpen(false);
  }, [isTemplateMode, templateId, campaignId, importToTemplateMutation, importToCampaignMutation, utilsForInvalidation]);

  // Sender (read-only) e config — usado pelo EmailDetailsBar
  const { data: senderConfig } = trpc.sendgrid.sender.get.useQuery();

  // Load campaign content
  useEffect(() => {
    if (!campaign) return;
    setEmailName(campaign.name ?? "Novo E-mail");
    setSubject(campaign.subject ?? "");
    setPreheader(campaign.preheader ?? "");
    setSegmentIds(Array.isArray(campaign.segmentIds) ? (campaign.segmentIds as number[]) : []);
    setTagIds(Array.isArray(campaign.tagIds) ? (campaign.tagIds as number[]) : []);
    if (campaign.contentJson && Array.isArray(campaign.contentJson) && (campaign.contentJson as Block[]).length > 0) {
      const loadedBlocks = (campaign.contentJson as Record<string, any>[])
        .map(b => normalizeBlock({ ...b, id: b.id || nanoid() }));
      setBlocks(loadedBlocks);
    }
  }, [campaign]);

  // Load template content (modo template). Templates não têm segmentIds/tagIds/preheader.
  useEffect(() => {
    if (!templateDoc) return;
    setEmailName(templateDoc.name ?? "Novo template");
    setSubject(templateDoc.subject ?? "");
    if (templateDoc.footer) setFooter(templateDoc.footer);
    const tplBlocks = (templateDoc.blocks as Record<string, any>[] | null | undefined) ?? [];
    if (tplBlocks.length > 0) {
      setBlocks(tplBlocks.map(b => normalizeBlock({ ...b, id: (b.id as string) || nanoid() })));
    } else if (templateDoc.html) {
      // Template legado salvo só como HTML cru — entra como um bloco customHtml.
      setBlocks([{ id: nanoid(), type: "customHtml", customHtml: templateDoc.html }]);
    }
  }, [templateDoc]);

  // Flush imperativo do bloco customHtml: lê outerHTML do iframe direto da
  // DOM e propaga pro estado, cancelando qualquer debounce pendente. O
  // CustomHtmlIframe registra/desregistra essa função via onRegisterFlush.
  // Usado por saveToCampaign/saveToTemplate/handleSaveAndAdvance pra
  // eliminar a corrida entre última tecla e clique em "Salvar"/"Enviar".
  const customHtmlFlushRef = useRef<(() => string | null) | null>(null);
  const registerCustomHtmlFlush = useCallback((fn: (() => string | null) | null) => {
    customHtmlFlushRef.current = fn;
  }, []);

  // Aplica o HTML flushado no array de blocos antes do save, sem depender
  // do setState assíncrono do React. Se não há bloco customHtml (ou flush
  // não registrado), retorna o array original inalterado.
  const blocksWithFlushedCustomHtml = useCallback((): Block[] => {
    const flushed = customHtmlFlushRef.current?.();
    if (!flushed) return blocks;
    return blocks.map(b => b.type === "customHtml" ? { ...b, customHtml: flushed } : b);
  }, [blocks]);

  // Save to campaign
  const saveToCampaign = useCallback(() => {
    if (!campaignId) return;
    const effectiveBlocks = blocksWithFlushedCustomHtml();
    const html = buildEmailHtml(effectiveBlocks, footer, design);
    updateCampaignMutation.mutate({
      id: campaignId,
      name: emailName,
      subject: subject || null,
      preheader: preheader || null,
      segmentIds,
      tagIds,
      contentJson: effectiveBlocks as unknown as Record<string, any>[],
      contentHtml: html,
    });
  }, [campaignId, blocksWithFlushedCustomHtml, footer, design, updateCampaignMutation, emailName, subject, preheader, segmentIds, tagIds]);

  // Save to template (modo /emails/templates/:id/edit)
  const saveToTemplate = useCallback(() => {
    if (!templateId) return;
    const effectiveBlocks = blocksWithFlushedCustomHtml();
    const html = buildEmailHtml(effectiveBlocks, footer, design);
    const text = effectiveBlocks.filter(b => b.type === "text").map(b => b.text ?? "").join("\n\n");
    updateTemplateMutation.mutate({
      id: templateId,
      name: emailName,
      subject: subject || emailName,
      html,
      text,
      blocks: effectiveBlocks as unknown as Record<string, any>[],
      footer,
    });
  }, [templateId, blocksWithFlushedCustomHtml, footer, design, updateTemplateMutation, emailName, subject]);

  const persistDoc = useCallback(() => {
    if (isTemplateMode) saveToTemplate();
    else if (campaignId) saveToCampaign();
  }, [isTemplateMode, saveToTemplate, campaignId, saveToCampaign]);

  useAutoSave(!!campaignId || !!templateId, dirty, persistDoc);

  // Mark dirty on changes
  const markDirty = useCallback(() => setDirty(true), []);

  // Block operations
  //
  // Invariante: e-mail composto por bloco `customHtml` (template importado)
  // é exclusivo — não mistura com blocos visuais. `buildEmailHtml` só faz
  // bypass do wrapper quando blocks tem exatamente 1 customHtml; misturar
  // injetaria <html>/<body>/<style> no meio do wrapper e quebraria o render.
  // Guardamos no add para forçar o usuário a apagar o customHtml primeiro.
  const refuseIfCustomHtmlExists = (): boolean => {
    if (blocks.some(b => b.type === "customHtml")) {
      toast.warning(
        "Este e-mail veio de um template HTML completo. Apague o bloco 'HTML personalizado' antes de adicionar blocos visuais.",
        { duration: 8000 },
      );
      return true;
    }
    return false;
  };

  const addBlock = (type: BlockType) => {
    if (refuseIfCustomHtmlExists()) return;
    const newBlock: Block = { id: nanoid(), type, ...BLOCK_DEFAULTS[type] };
    setBlocks(prev => [...prev, newBlock]);
    setSelectedBlockId(newBlock.id);
    markDirty();
  };

  // Insere um bloco em índice específico — usado pelo drop do painel no canvas.
  const addBlockAt = (type: BlockType, index: number) => {
    if (refuseIfCustomHtmlExists()) return;
    const newBlock: Block = { id: nanoid(), type, ...BLOCK_DEFAULTS[type] };
    setBlocks(prev => {
      const out = [...prev];
      const safeIdx = Math.max(0, Math.min(index, out.length));
      out.splice(safeIdx, 0, newBlock);
      return out;
    });
    setSelectedBlockId(newBlock.id);
    markDirty();
  };

  const removeBlock = (id: string) => {
    setBlocks(prev => prev.filter(b => b.id !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
    markDirty();
  };

  const updateBlock = (id: string, updates: Partial<Block>) => {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
    markDirty();
  };

  /**
   * O Assistente enxerga o e-mail aberto e escreve nele.
   *
   * Só os blocos de texto e cabeçalho entram como conteúdo editável: são os
   * que carregam copy. Imagem, botão e divisor aparecem como estrutura, para
   * ele saber onde as coisas estão sem receber base64 nem estilo.
   *
   * Nada é salvo: as ações mexem no editor e o rascunho fica sujo, como se a
   * pessoa tivesse digitado. Quem aperta Salvar continua sendo ela.
   */
  useContextoDaTela(
    {
      titulo: `E-mail: ${emailName}`,
      dados: {
        campaignId: campaignId ?? null,
        templateId: templateId ?? null,
        assunto: subject,
        previa: preheader,
        blocoSelecionado: selectedBlockId,
        blocos: blocks.map((b) => ({
          id: b.id,
          tipo: b.type,
          texto: (b.type === "header"
            ? [b.title, b.subtitle].filter(Boolean).join(" — ")
            : b.type === "button"
              ? b.btnText
              : b.type === "list"
                ? b.listItems
                : b.type === "customHtml"
                  ? b.customHtml
                  : b.text
          )?.slice(0, 600) ?? null,
        })),
      },
      acoes: [
        { id: "definir_assunto", descricao: "Troca o assunto do e-mail aberto.", parametros: { texto: "o assunto" } },
        { id: "definir_previa", descricao: "Troca a prévia (preheader) do e-mail aberto.", parametros: { texto: "a prévia" } },
        {
          id: "escrever_bloco",
          descricao:
            "Substitui o texto de um bloco pelo id (veja `blocos`): texto, botão (o rótulo), lista (um item por linha) e HTML personalizado. Cabeçalho aceita titulo e subtitulo. Imagem, vídeo, divisor e espaçador não têm texto.",
          parametros: { blocoId: "id do bloco", texto: "o novo texto", titulo: "só para cabeçalho", subtitulo: "só para cabeçalho" },
        },
      ],
    },
    {
      definir_assunto: (args) => {
        setSubject(String(args.texto ?? ""));
        markDirty();
      },
      definir_previa: (args) => {
        setPreheader(String(args.texto ?? ""));
        markDirty();
      },
      escrever_bloco: (args) => {
        const id = String(args.blocoId ?? "");
        const alvo = blocks.find((b) => b.id === id);
        if (!alvo) throw new Error("Bloco não encontrado — liste os blocos de novo.");
        const texto = String(args.texto ?? "");
        // Cada tipo guarda a copy num campo próprio. Escrever `text` num bloco
        // de botão ou de HTML não mudava nada na tela e ainda assim voltava
        // "ok" — o assistente dizia ter reescrito o e-mail sem ter reescrito.
        switch (alvo.type) {
          case "header":
            updateBlock(id, {
              ...(args.titulo !== undefined ? { title: String(args.titulo) } : {}),
              ...(args.subtitulo !== undefined ? { subtitle: String(args.subtitulo) } : {}),
              ...(args.titulo === undefined && args.texto !== undefined ? { title: texto } : {}),
            });
            break;
          case "text":
            updateBlock(id, { text: texto });
            break;
          case "button":
            updateBlock(id, { btnText: texto });
            break;
          case "list":
            updateBlock(id, { listItems: texto });
            break;
          case "customHtml":
            updateBlock(id, { customHtml: texto });
            break;
          default:
            throw new Error(
              `O bloco "${alvo.type}" não tem texto para escrever (é imagem, vídeo, divisor ou espaçador). Use a tela para trocá-lo.`,
            );
        }
        setSelectedBlockId(id);
      },
    },
  );

  const moveBlock = (id: string, direction: "up" | "down") => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === id);
      if (idx < 0) return prev;
      if (direction === "up" && idx === 0) return prev;
      if (direction === "down" && idx === prev.length - 1) return prev;
      const newBlocks = [...prev];
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      [newBlocks[idx], newBlocks[swapIdx]] = [newBlocks[swapIdx], newBlocks[idx]];
      return newBlocks;
    });
    markDirty();
  };

  const duplicateBlock = (id: string) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === id);
      if (idx < 0) return prev;
      const clone = { ...prev[idx], id: nanoid() };
      const newBlocks = [...prev];
      newBlocks.splice(idx + 1, 0, clone);
      return newBlocks;
    });
    markDirty();
  };

  // ── Drag-and-drop (reordenar + adicionar do painel) ──────────────────────
  // `activeDragId` armazena o ID arrastado pra dois fins:
  //   1) Saber se é um bloco do canvas (`blockId`) ou um botão do painel
  //      (`panel:<type>`). Quando vem do painel, ativamos as drop-zones entre
  //      os blocos do canvas.
  //   2) Renderizar `<DragOverlay>` com preview visual do que está sendo
  //      arrastado (quando do painel, mostra um "fantasma" do bloco).
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const isPanelDrag = activeDragId?.startsWith("panel:") ?? false;

  function handleDragStart(e: DragStartEvent) {
    setActiveDragId(String(e.active.id));
  }

  function handleDragEnd(e: DragEndEvent) {
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    setActiveDragId(null);
    if (!overId) return;

    // Caso 1: arrastando um botão do painel pra dentro do canvas.
    if (activeId.startsWith("panel:") && overId.startsWith("dropzone:")) {
      const type = activeId.slice("panel:".length) as BlockType;
      const idx = parseInt(overId.slice("dropzone:".length), 10) || 0;
      addBlockAt(type, idx);
      return;
    }

    // Caso 2: reordenar blocos do canvas (active e over são block IDs).
    if (!activeId.startsWith("panel:") && !overId.startsWith("dropzone:") && activeId !== overId) {
      setBlocks(prev => {
        const oldIdx = prev.findIndex(b => b.id === activeId);
        const newIdx = prev.findIndex(b => b.id === overId);
        if (oldIdx < 0 || newIdx < 0) return prev;
        return arrayMove(prev, oldIdx, newIdx);
      });
      markDirty();
    }
  }

  const handleSave = () => {
    if (isTemplateMode) {
      saveToTemplate();
      toast.success("Template salvo!");
    } else if (campaignId) {
      saveToCampaign();
      toast.success("Salvo!");
    } else {
      setDirty(false);
      setLastSaved(new Date());
      toast.success("Salvo!");
    }
  };

  const handleSaveAndAdvance = async () => {
    if (!campaignId) {
      toast.warning("Salve o e-mail antes de enviar.");
      return;
    }
    // Awaita a mutation antes de navegar. Sem isso, a página de envio
    // carrega o `campaign.contentHtml` do DB que pode ainda estar com a
    // versão anterior (mutation async não completou). Também usa o
    // helper `blocksWithFlushedCustomHtml` pra eliminar a corrida com o
    // debounce do iframe — edits inline são serializados síncronamente
    // direto do DOM.
    const effectiveBlocks = blocksWithFlushedCustomHtml();
    const html = buildEmailHtml(effectiveBlocks, footer, design);
    try {
      await updateCampaignMutation.mutateAsync({
        id: campaignId,
        name: emailName,
        subject: subject || null,
        preheader: preheader || null,
        segmentIds,
        tagIds,
        contentJson: effectiveBlocks as unknown as Record<string, any>[],
        contentHtml: html,
      });
    } catch {
      // `onError` da mutation já notifica via toast.
      return;
    }
    navigate(`/emails/${campaignId}/send`);
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) { toast.error("Nome do template é obrigatório"); return; }
    const html = buildEmailHtml(blocks, footer, design);
    const text = blocks.filter(b => b.type === "text").map(b => b.text ?? "").join("\n\n");
    saveTemplateMutation.mutate({
      name: templateName.trim(),
      subject: emailName,
      html,
      text,
      blocks: blocks as unknown as Record<string, any>[],
      footer,
    });
  };

  const handleExit = () => {
    navigate(isTemplateMode ? "/emails/templates" : "/emails");
  };

  const selectedBlock = blocks.find(b => b.id === selectedBlockId);
  const previewWidth = previewMode === "desktop" ? design.contentWidth : 320;
  const html = buildEmailHtml(blocks, footer, design);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <PermissionGuard permission="email:view_campaigns">
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between h-12 px-4 border-b bg-background shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-primary">
            <Mail className="h-4 w-4" />
            <span>Marketing</span>
          </div>
          <Separator orientation="vertical" className="h-5" />
          <span className="text-sm text-muted-foreground">
            Email Marketing: <span className="text-foreground font-medium">{emailName}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-xs gap-1.5" onClick={handleExit}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Sair do editor
          </Button>
        </div>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between h-10 px-4 border-b bg-muted/30 shrink-0">
        <div className="flex items-center gap-2">
          <Button
            variant={showLeadCopy ? "secondary" : "ghost"}
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={() => setShowLeadCopy(!showLeadCopy)}
          >
            <Sparkles className="h-3.5 w-3.5" />
            {showLeadCopy ? "Ocultar LeadCopy" : "Exibir LeadCopy"}
          </Button>
          <Button
            variant={showStructure ? "secondary" : "ghost"}
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={() => setShowStructure(!showStructure)}
          >
            <Eye className="h-3.5 w-3.5" />
            {showStructure ? "Ocultar estrutura" : "Exibir estrutura"}
          </Button>
          <Separator orientation="vertical" className="h-4" />
          {lastSaved && (
            <span className="text-xs text-muted-foreground">
              Salvo às {lastSaved.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          {dirty && !lastSaved && (
            <span className="text-xs text-amber-600">Alterações não salvas</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Desktop / Mobile toggle */}
          <div className="flex items-center border rounded-md overflow-hidden">
            <button
              className={`p-1.5 ${previewMode === "desktop" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              onClick={() => setPreviewMode("desktop")}
              title="Desktop"
            >
              <Monitor className="h-3.5 w-3.5" />
            </button>
            <button
              className={`p-1.5 ${previewMode === "mobile" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              onClick={() => setPreviewMode("mobile")}
              title="Mobile"
            >
              <Smartphone className="h-3.5 w-3.5" />
            </button>
          </div>

          <Separator orientation="vertical" className="h-4" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Mais opções">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setImportHtmlOpen(true)}>
                <Code2 className="h-3.5 w-3.5 mr-2" />
                Importar HTML
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {!isTemplateMode && (
                <>
                  <DropdownMenuItem onClick={() => setShowSaveDialog(true)}>
                    <BookmarkPlus className="h-3.5 w-3.5 mr-2" />
                    Salvar como template
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => {
                const blob = new Blob([html], { type: "text/html" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${emailName}.html`;
                a.click();
                URL.revokeObjectURL(url);
              }}>
                Exportar HTML
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={handleSave}>
            <Save className="h-3.5 w-3.5" />
            Salvar
          </Button>

          {campaignId && !isTemplateMode && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={() => setAttachToFlowOpen(true)}
              title="Anexar este e-mail a um fluxo de automação"
            >
              <GitBranch className="h-3.5 w-3.5" />
              Anexar a um fluxo
            </Button>
          )}

          {!isTemplateMode && (
            <Button size="sm" className="h-7 text-xs gap-1.5" onClick={handleSaveAndAdvance}>
              <Send className="h-3.5 w-3.5" />
              Enviar / Agendar
            </Button>
          )}
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      {/* DndContext envolve painel esquerdo + canvas: assim o usuário pode
          arrastar um botão do painel pra dentro do canvas (drop-zones) e/ou
          reordenar blocos do canvas (SortableContext interno). */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDragId(null)}
      >
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left Panel (3 tabs) ─────────────────────────────────────────── */}
        <div className="w-[300px] border-r bg-background flex flex-col shrink-0 overflow-hidden">
          {selectedBlock ? (
            /* Properties panel when block selected */
            <div className="flex flex-col h-full overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {(() => { const Icon = BLOCK_ICONS[selectedBlock.type]; return <Icon className="w-4 h-4 text-primary" />; })()}
                  {BLOCK_LABELS[selectedBlock.type]}
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedBlockId(null)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                <BlockEditor block={selectedBlock} onChange={(updates) => updateBlock(selectedBlock.id, updates)} />
              </div>
              <div className="border-t p-2 flex gap-1 justify-center">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveBlock(selectedBlock.id, "up")} title="Mover para cima">
                  <MoveUp className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveBlock(selectedBlock.id, "down")} title="Mover para baixo">
                  <MoveDown className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateBlock(selectedBlock.id)} title="Duplicar">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Separator orientation="vertical" className="h-5 mx-1" />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeBlock(selectedBlock.id)} title="Excluir">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            /* Tabs: Conteúdo / Linhas / Configuração */
            <Tabs value={leftTab} onValueChange={v => setLeftTab(v as any)} className="flex flex-col h-full">
              <TabsList className="w-full rounded-none border-b h-9 bg-transparent justify-start px-1 shrink-0">
                <TabsTrigger value="content" className="text-xs gap-1.5 data-[state=active]:bg-muted">
                  <LayoutTemplate className="h-3.5 w-3.5" />
                  Conteúdo
                </TabsTrigger>
                <TabsTrigger value="rows" className="text-xs gap-1.5 data-[state=active]:bg-muted">
                  <Rows3 className="h-3.5 w-3.5" />
                  Linhas
                </TabsTrigger>
                <TabsTrigger value="settings" className="text-xs gap-1.5 data-[state=active]:bg-muted">
                  <Settings2 className="h-3.5 w-3.5" />
                  Config
                </TabsTrigger>
              </TabsList>

              {/* Aba CONTEÚDO */}
              <TabsContent value="content" className="flex-1 overflow-y-auto p-3 mt-0">
                <p className="text-xs text-muted-foreground mb-3">Arraste ou clique para adicionar blocos ao e-mail</p>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(BLOCK_LABELS) as BlockType[])
                    // O bloco "customHtml" é gerado por templates legados — não
                    // expomos como opção pro usuário adicionar manualmente.
                    .filter(t => t !== "customHtml")
                    .map(type => (
                      <PanelBlockButton
                        key={type}
                        type={type}
                        Icon={BLOCK_ICONS[type]}
                        label={BLOCK_LABELS[type]}
                        onAdd={() => addBlock(type)}
                      />
                    ))}
                </div>
              </TabsContent>

              {/* Aba LINHAS */}
              <TabsContent value="rows" className="flex-1 overflow-y-auto p-3 mt-0">
                <p className="text-xs text-muted-foreground mb-3">Layouts de estrutura predefinidos</p>
                <div className="space-y-2">
                  {ROW_LAYOUTS.map(layout => (
                    <button
                      key={layout.id}
                      onClick={() => {
                        if (layout.columns.length === 1) {
                          addBlock("text");
                        } else {
                          addBlock("columns");
                        }
                        toast.success(`Layout "${layout.label}" adicionado`);
                      }}
                      className="flex items-center gap-3 w-full p-3 rounded-lg border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors"
                    >
                      <div className="flex gap-1 flex-1">
                        {layout.columns.map((w, i) => (
                          <div
                            key={i}
                            className="h-8 rounded bg-muted border"
                            style={{ width: `${w}%` }}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{layout.label}</span>
                    </button>
                  ))}
                </div>
              </TabsContent>

              {/* Aba CONFIGURAÇÃO */}
              <TabsContent value="settings" className="flex-1 overflow-y-auto p-3 mt-0">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs">Largura do conteúdo (px)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="range" min={400} max={800} step={10}
                        value={design.contentWidth}
                        onChange={e => { setDesign(d => ({ ...d, contentWidth: Number(e.target.value) })); markDirty(); }}
                        className="flex-1"
                      />
                      <span className="text-xs text-muted-foreground w-10 text-right">{design.contentWidth}</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Alinhamento</Label>
                    <div className="flex gap-1 mt-1">
                      {(["left", "center"] as const).map(a => (
                        <Button key={a} variant={design.contentAlign === a ? "default" : "outline"} size="sm" className="h-7 px-3 text-xs" onClick={() => { setDesign(d => ({ ...d, contentAlign: a })); markDirty(); }}>
                          {a === "left" ? "Esquerda" : "Centro"}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <Label className="text-xs">Cor de fundo da página</Label>
                    <div className="flex gap-1.5 mt-1">
                      <input type="color" value={design.bgColor} onChange={e => { setDesign(d => ({ ...d, bgColor: e.target.value })); markDirty(); }} className="w-8 h-8 rounded border cursor-pointer" />
                      <Input value={design.bgColor} onChange={e => { setDesign(d => ({ ...d, bgColor: e.target.value })); markDirty(); }} className="h-8 text-xs font-mono" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Cor de fundo do conteúdo</Label>
                    <div className="flex gap-1.5 mt-1">
                      <input type="color" value={design.contentBgColor} onChange={e => { setDesign(d => ({ ...d, contentBgColor: e.target.value })); markDirty(); }} className="w-8 h-8 rounded border cursor-pointer" />
                      <Input value={design.contentBgColor} onChange={e => { setDesign(d => ({ ...d, contentBgColor: e.target.value })); markDirty(); }} className="h-8 text-xs font-mono" />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <Label className="text-xs">Fonte padrão</Label>
                    <Select value={design.fontFamily} onValueChange={v => { setDesign(d => ({ ...d, fontFamily: v })); markDirty(); }}>
                      <SelectTrigger className="mt-1 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_OPTIONS.map(f => (
                          <SelectItem key={f} value={f} className="text-xs" style={{ fontFamily: f }}>
                            {f.split(",")[0]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Cor dos links</Label>
                    <div className="flex gap-1.5 mt-1">
                      <input type="color" value={design.linkColor} onChange={e => { setDesign(d => ({ ...d, linkColor: e.target.value })); markDirty(); }} className="w-8 h-8 rounded border cursor-pointer" />
                      <Input value={design.linkColor} onChange={e => { setDesign(d => ({ ...d, linkColor: e.target.value })); markDirty(); }} className="h-8 text-xs font-mono" />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <Label className="text-xs">Rodapé do e-mail</Label>
                    <Textarea
                      value={footer}
                      onChange={e => { setFooter(e.target.value); markDirty(); }}
                      rows={3}
                      className="mt-1 text-xs"
                      placeholder="© 2025 Empresa. Todos os direitos reservados."
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* ── Canvas Central ──────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Barra de detalhes do e-mail (assunto / preheader / destinatários / remetente).
              Em modo template, mostramos versão simplificada: só assunto. Templates não têm
              segmentação, remetente nem preheader. */}
          {isTemplateMode ? (
            <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/20 shrink-0">
              <Label className="text-xs text-muted-foreground shrink-0">Assunto</Label>
              <Input
                value={subject}
                onChange={(e) => { setSubject(e.target.value); markDirty(); }}
                placeholder="Assunto do template"
                className="h-8 text-sm"
              />
            </div>
          ) : (
            <EmailDetailsBar
              subject={subject}
              onSubjectChange={(v) => { setSubject(v); markDirty(); }}
              preheader={preheader}
              onPreheaderChange={(v) => { setPreheader(v); markDirty(); }}
              segmentIds={segmentIds}
              onSegmentIdsChange={(ids) => { setSegmentIds(ids); markDirty(); }}
              tagIds={tagIds}
              onTagIdsChange={(ids) => { setTagIds(ids); markDirty(); }}
              senderName={(senderConfig as { fromName?: string } | undefined)?.fromName}
              senderEmail={(senderConfig as { fromEmail?: string } | undefined)?.fromEmail}
            />
          )}
          {campaignId && !isTemplateMode && (abFromUrl || (campaign as { type?: string } | undefined)?.type === "ab_test") && (
            <ABTestPanel campaignId={campaignId} />
          )}
        <div className="flex-1 overflow-auto" style={{ backgroundColor: design.bgColor }}>
          <div className="flex flex-col items-center py-6 min-h-full">
            {/* Preview container */}
            <div
              className="transition-all duration-300"
              style={{ width: previewWidth, maxWidth: "100%" }}
            >
              {blocks.length === 0 ? (
                /* Empty state */
                <div
                  className="border-2 border-dashed border-muted-foreground/20 rounded-lg bg-white/60 backdrop-blur-sm flex flex-col items-center justify-center py-20"
                >
                  <Plus className="w-12 h-12 text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground font-medium">Solte blocos de conteúdo aqui</p>
                  <p className="text-xs text-muted-foreground mt-1">Use o painel à esquerda para adicionar blocos</p>
                </div>
              ) : (
                <div
                  className="shadow-lg rounded-sm overflow-hidden"
                  style={{ backgroundColor: design.contentBgColor }}
                >
                  <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    {/* Drop-zone inicial (acima do primeiro bloco) — só vira
                        alvo visível quando arrastando do painel. */}
                    <CanvasDropZone index={0} active={isPanelDrag} />
                    {blocks.map((block, idx) => {
                      const isSelected = selectedBlockId === block.id;
                      return (
                        <Fragment key={block.id}>
                          <SortableBlock
                            block={block}
                            isSelected={isSelected}
                            onSelect={() => setSelectedBlockId(block.id)}
                            controls={
                              <>
                                <div className={`absolute top-1 right-1 flex gap-0.5 ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>
                                  <button
                                    className="p-1 bg-background/90 border rounded shadow-sm hover:bg-muted"
                                    onClick={e => { e.stopPropagation(); moveBlock(block.id, "up"); }}
                                    title="Mover para cima"
                                  >
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    className="p-1 bg-background/90 border rounded shadow-sm hover:bg-muted"
                                    onClick={e => { e.stopPropagation(); moveBlock(block.id, "down"); }}
                                    title="Mover para baixo"
                                  >
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                  <button
                                    className="p-1 bg-background/90 border rounded shadow-sm hover:bg-muted"
                                    onClick={e => { e.stopPropagation(); duplicateBlock(block.id); }}
                                    title="Duplicar"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                  <button
                                    className="p-1 bg-background/90 border rounded shadow-sm hover:bg-red-50 hover:text-destructive"
                                    onClick={e => { e.stopPropagation(); removeBlock(block.id); }}
                                    title="Excluir"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                                <div className={`absolute top-1 left-9 ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>
                                  <span className="text-[10px] font-medium bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                                    {BLOCK_LABELS[block.type]}
                                  </span>
                                </div>
                              </>
                            }
                          >
                            <EditableBlockCanvas
                              block={block}
                              design={design}
                              onUpdate={(updates) => updateBlock(block.id, updates)}
                              isSelected={isSelected}
                              onActivate={() => setSelectedBlockId(block.id)}
                              onRegisterCustomHtmlFlush={registerCustomHtmlFlush}
                            />
                          </SortableBlock>
                          <CanvasDropZone index={idx + 1} active={isPanelDrag} />
                        </Fragment>
                      );
                    })}
                  </SortableContext>

                  {/* Footer preview do sistema. Só renderiza quando o e-mail
                      é composto por blocos visuais — quando há um bloco
                      customHtml (template importado), o e-mail tem seu próprio
                      rodapé e o `buildEmailHtml` faz bypass do wrapper. Manter
                      esse rodapé aqui duplicaria o footer visualmente E não
                      seria enviado de fato. */}
                  {!blocks.some(b => b.type === "customHtml") && (
                    <div
                      className="pointer-events-none"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(`<div style="background:#f9fafb;padding:16px 24px;text-align:center;font-size:12px;color:#9ca3af;border-top:1px solid #e5e7eb;font-family:${design.fontFamily}">${footer || "© 2025 Empresa. Todos os direitos reservados."}<br><a href="#" style="color:#9ca3af;text-decoration:underline">Cancele sua inscrição</a></div>`)
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* ── LeadCopy Panel (assistente conversacional) ──────────────────── */}
        {showLeadCopy && campaignId && !isTemplateMode && (
          <div className="w-[360px] border-l bg-background flex flex-col shrink-0 overflow-hidden">
            <LeadCopyPanel
              mode="email"
              contextRef={{
                kind: "email",
                id: campaignId,
                subject,
                preheader,
                // bodySnippet alimenta o contexto da IA. Para blocos visuais,
                // junta o texto dos blocks. Para customHtml (template importado),
                // extrai o texto visível removendo tags e scripts.
                bodySnippet: (() => {
                  const customHtml = blocks.find((b) => b.type === "customHtml")?.customHtml;
                  if (customHtml) {
                    return customHtml
                      .replace(/<script[\s\S]*?<\/script>/gi, "")
                      .replace(/<style[\s\S]*?<\/style>/gi, "")
                      .replace(/<[^>]+>/g, " ")
                      .replace(/&nbsp;/gi, " ")
                      .replace(/\s+/g, " ")
                      .trim()
                      .slice(0, 1500);
                  }
                  return blocks
                    .filter((b) => b.type === "text" || b.type === "header")
                    .map((b) => (b as any).text ?? "")
                    .join("\n\n")
                    .slice(0, 1500);
                })(),
              }}
              onApplyAction={(action: ChatAction) => {
                if (action.kind !== "applyToEmail") return;
                if (action.field === "subject") {
                  setSubject(action.value);
                  markDirty();
                  toast.success("Assunto aplicado.");
                } else if (action.field === "preheader") {
                  setPreheader(action.value);
                  markDirty();
                  toast.success("Preheader aplicado.");
                } else if (action.field === "body") {
                  // Quando há customHtml (template importado), substituir blocos
                  // destruiria todo o design importado. Bloqueia e avisa o usuário
                  // — assunto/preheader continuam aplicáveis pelo mesmo handler.
                  if (blocks.some((b) => b.type === "customHtml")) {
                    toast.warning(
                      "Este e-mail foi importado de HTML completo — aplicar um corpo gerado iria apagar o design. Use 'Substituir texto' no painel esquerdo pra editar trechos específicos.",
                      { duration: 10000 },
                    );
                    return;
                  }
                  // Converte texto/markdown leve em blocos do editor.
                  // Estratégia: parágrafos (separados por linha em branco) viram blocks "text";
                  // sequência de linhas começando com "- " viram um block "list" único.
                  const newBlocks: Block[] = [];
                  // Preserva o header existente (se houver) pra não perder branding/visual.
                  const existingHeader = blocks.find((b) => b.type === "header");
                  if (existingHeader) newBlocks.push(existingHeader);

                  const paragraphs = action.value
                    .replace(/\r\n/g, "\n")
                    .split(/\n\s*\n/)
                    .map((p) => p.trim())
                    .filter(Boolean);

                  for (const para of paragraphs) {
                    const lines = para.split("\n").map((l) => l.trim());
                    const isList = lines.length >= 2 && lines.every((l) => /^[-*•]\s+/.test(l));
                    if (isList) {
                      const items = lines.map((l) => l.replace(/^[-*•]\s+/, "")).join("\n");
                      newBlocks.push({
                        id: nanoid(),
                        type: "list",
                        ...BLOCK_DEFAULTS.list,
                        listItems: items,
                        listStyle: "bullet",
                      });
                    } else {
                      newBlocks.push({
                        id: nanoid(),
                        type: "text",
                        ...BLOCK_DEFAULTS.text,
                        text: para,
                      });
                    }
                  }

                  // Preserva o botão CTA existente se já existia (geralmente o último).
                  const existingButton = [...blocks].reverse().find((b) => b.type === "button");
                  if (existingButton) newBlocks.push(existingButton);

                  if (newBlocks.length === 0) {
                    toast.warning("Corpo vazio — nada aplicado.");
                    return;
                  }
                  setBlocks(newBlocks);
                  markDirty();
                  toast.success(`Corpo do e-mail aplicado (${newBlocks.length} bloco${newBlocks.length === 1 ? "" : "s"}).`);
                }
              }}
            />
          </div>
        )}

        {/* ── Structure Panel (optional, when "Exibir estrutura" is on) ──── */}
        {showStructure && (
          <div className="w-[250px] border-l bg-background flex flex-col shrink-0 overflow-hidden">
            <div className="px-3 py-2 border-b bg-muted/30">
              <h3 className="text-xs font-semibold">Estrutura do E-mail</h3>
              <p className="text-xs text-muted-foreground">{blocks.length} bloco(s)</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {blocks.map((block, idx) => {
                const Icon = BLOCK_ICONS[block.type];
                const isSelected = selectedBlockId === block.id;
                return (
                  <div
                    key={block.id}
                    onClick={() => setSelectedBlockId(block.id)}
                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                      isSelected ? "bg-primary/10 text-primary border border-primary/30" : "hover:bg-muted"
                    }`}
                  >
                    <GripVertical className="w-3 h-3 text-muted-foreground/40 shrink-0" />
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="flex-1 truncate text-xs">{BLOCK_LABELS[block.type]}</span>
                    <span className="text-[10px] text-muted-foreground">{idx + 1}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* DragOverlay: preview visual do que está sendo arrastado.
          Sem isso o arrasto fica "invisível" enquanto solto. */}
      <DragOverlay dropAnimation={null}>
        {activeDragId?.startsWith("panel:") ? (
          <div className="rounded-lg border-2 border-primary bg-primary/10 px-4 py-3 text-xs font-medium text-primary shadow-lg">
            {BLOCK_LABELS[activeDragId.slice("panel:".length) as BlockType] ?? "Bloco"}
          </div>
        ) : null}
      </DragOverlay>
      </DndContext>

      {/* Toolbar flutuante de formatação inline (B / I / Link + alinhamento /
          tamanho / cor do texto pra blocos `text`) */}
      <InlineFormatToolbar blocks={blocks} onUpdateBlock={updateBlock} />

      {/* ── Attach To Flow Dialog ──────────────────────────────────────────── */}
      {campaignId && !isTemplateMode && (
        <AttachToFlowDialog
          open={attachToFlowOpen}
          onOpenChange={setAttachToFlowOpen}
          campaignId={campaignId}
          campaignName={emailName}
          campaignSubject={subject}
        />
      )}

      {/* SendEmailDialog removido — agora é página dedicada em /emails/:id/send.
          Editor não bloqueia mais o usuário durante o envio. */}

      {/* ── Save as Template Dialog ────────────────────────────────────────── */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Salvar como Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Nome do Template *</Label>
              <Input
                className="mt-1"
                placeholder="Ex: Newsletter Mensal"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSaveTemplate()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSaveDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveTemplate} disabled={saveTemplateMutation.isPending}>
              {saveTemplateMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Import HTML Dialog ───────────────────────────────────────────── */}
      <ImportHtmlDialog
        open={importHtmlOpen}
        onClose={() => setImportHtmlOpen(false)}
        onImport={handleImportHtmlInEditor}
        hasExistingContent={blocks.length > 0}
        isSubmitting={importToCampaignMutation.isPending || importToTemplateMutation.isPending}
      />
    </div>
    </PermissionGuard>
  );
}
